// ApiMarcajes.js
import axios from 'axios';
import { obtenerTurnosPorCuadrante } from '../data/FirestoreCuadrantes';

// Configuración de la API
const freematicaConfig = {
  token: '6075893bb0224601a40f5e9fd1723864',
  apiUrl: 'https://api-p02.clientservicepanel.com/restsat/api',
  appId: '87452890|6484502060e844cdba2cc6d5ab8c02a2',
  organization: '0109',
  company: '0001'
};


// Headers comunes para todas las peticiones
const getHeaders = (empresa = freematicaConfig.company) => {
  // IMPORTANTE: El header x-auth-company SIEMPRE es 0001 (autenticación)
  // La empresa del contrato se usa en las queries FIQL, NO en los headers
  const empresaAuth = '0001'; // Siempre fijo para autenticación
  
  const headers = {
    'x-auth-token': freematicaConfig.token,
    'x-auth-app': freematicaConfig.appId,
    'x-auth-organization': freematicaConfig.organization,
    'x-auth-company': empresaAuth, // ✅ Siempre 0001 (autenticación)
    'x-auth-session': freematicaConfig.token,
    'Content-Type': 'application/json; charset=utf-8'
  };
  
  // Log de headers para debugging
  console.log('🔧 Headers configurados:', {
    'x-auth-token': headers['x-auth-token'],
    'x-auth-app': headers['x-auth-app'],
    'x-auth-organization': headers['x-auth-organization'],
    'x-auth-company': `${headers['x-auth-company']} (fija para auth)`,
    'x-auth-session': headers['x-auth-session']
  });
  
  return headers;
};

/**
 * Obtiene la lista de horarios operativos de contratos de Freematica
 * @param {Array} filtrosContratos - Array de filtros en formato "contrato/servicio" (ej: ["12901/01", "14091/02"])
 * @param {string} empresa - Código de empresa (opcional, por defecto usa la configurada)
 * @returns {Promise<Object>} - Objeto con errorCode, errorMessage y data
 */
/**
 * Obtiene los cuadrantes detalles (días concretos ya calculados) de Freematica
 * @param {Array} filtrosContratos - Array de filtros en formato "contrato/servicio" (ej: ["13550/03"])
 * @param {string} empresa - Código de empresa (opcional, por defecto usa la configurada)
 * @param {number} mes - Mes (1-12, opcional)
 * @param {number} año - Año (opcional)
 * @returns {Promise<Object>} - Objeto con errorCode, errorMessage y data
 */
export const obtenerCuadrantesDetalles = async (filtrosContratos = [], empresa = freematicaConfig.company, mes = null, año = null) => {
  try {
    console.log('🔄 Iniciando fetch de cuadrantes-detalles (días concretos)...');
    
    const headers = getHeaders(empresa);
    let url = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes-detalles`;
    
    // Construir query FIQL
    const filtrosQuery = [];
    
    // Si se proporcionan filtros de contrato/servicio
    if (filtrosContratos && filtrosContratos.length > 0) {
      console.log(`📋 Filtrando por contratos/servicios: ${filtrosContratos.join(', ')}`);
      
      const filtrosFIQL = filtrosContratos.map(filtro => {
        if (filtro.includes('/')) {
          const [contrato, servicio] = filtro.split('/');
          return `CUADD_CTRT==${contrato};CUADD_SERV==${servicio}`;
        } else {
          return `CUADD_CTRT==${filtro}`;
        }
      });
      
      filtrosQuery.push(...filtrosFIQL);
    }
    
    // Agregar filtros de mes y año si se proporcionan
    if (año) {
      filtrosQuery.push(`CUADD_CAL==${año}`);
      console.log(`📅 Filtrando por año: ${año}`);
    }
    
    if (mes) {
      filtrosQuery.push(`CUADD_MES==${mes}`);
      console.log(`📅 Filtrando por mes: ${mes}`);
    }
    
    // Combinar filtros
    if (filtrosQuery.length > 0) {
      const queryFIQL = filtrosQuery.join(';'); // AND entre filtros
      url += `?rquery=${encodeURIComponent(queryFIQL)}`;
      console.log(`🔍 Query FIQL construida: ${queryFIQL}`);
    }
    
    console.log(`🌐 URL de petición: ${url}`);
    console.log('🔧 Headers que se envían:', JSON.stringify(headers, null, 2));
    
    const response = await axios.get(url, { headers });
    
    if (response.status === 200 && response.data) {
      console.log(`✅ Respuesta exitosa de cuadrantes-detalles`);
      console.log(`📊 Total de registros: ${response.data.data?.total || 0}`);
      console.log(`📋 Items recibidos: ${response.data.data?.items?.length || 0}`);
      
      return {
        errorCode: "200",
        errorMessage: "",
        data: response.data.data || { items: [], total: 0 }
      };
    } else {
      console.error('❌ Respuesta no exitosa:', response.status);
      return {
        errorCode: String(response.status),
        errorMessage: `Error HTTP ${response.status}`,
        data: null
      };
    }
    
  } catch (error) {
    console.error('❌ Error en obtenerCuadrantesDetalles:', error);
    return {
      errorCode: error.response?.status ? String(error.response.status) : "500",
      errorMessage: error.message || 'Error desconocido',
      data: null
    };
  }
};

export const obtenerHorariosOperativosContratos = async (filtrosContratos = [], empresa = freematicaConfig.company) => {
  try {
    console.log('🔄 Iniciando fetch de horarios operativos de contratos...');
    
    const headers = getHeaders(empresa);
    let url = `${freematicaConfig.apiUrl}/pvss/v1/contratos-horarios-operativa`;
    
    // Si se proporcionan filtros específicos, construir query FIQL
    if (filtrosContratos && filtrosContratos.length > 0) {
      console.log(`📋 Filtrando por contratos/servicios: ${filtrosContratos.join(', ')}`);
      
      // Construir filtros FIQL para cada contrato/servicio
      const filtrosFIQL = filtrosContratos.map(filtro => {
        const [contrato, servicio] = filtro.split('/');
        return `CTSHB_CTRT==${contrato};CTSHB_SERV==${servicio}`;
      });
      
      // Combinar todos los filtros con OR
      const queryFIQL = filtrosFIQL.join(',');
      url += `?rquery=${encodeURIComponent(queryFIQL)}`;
      
      console.log(`🔍 Query FIQL construida: ${queryFIQL}`);
    }
    
    console.log(`🌐 URL de petición: ${url}`);
    console.log('🔧 Headers que se envían:', JSON.stringify(headers, null, 2));
    
    const response = await axios.get(url, { headers });
    
    if (response.status === 200 && response.data) {
      const data = response.data;
      // Normalizar: la API puede devolver { data: { total, items } } o { errorCode, data: { total, items } }
      const payload = data.data || data;
      const items = payload?.items ?? [];
      const total = payload?.total ?? items.length;
      console.log(`✅ Horarios operativos obtenidos exitosamente`);
      console.log(`📊 Total de registros: ${total}`);
      console.log(`📋 Items en respuesta: ${items.length}`);
      
      if (items.length > 0) {
        console.log('📊 Primeros registros obtenidos:');
        items.slice(0, 3).forEach((item, index) => {
          console.log(`📋 Registro ${index + 1}:`, {
            contrato: item.CTSHB_CTRT,
            delegacion: item.CTSHB_DELEG,
            servicio: item.CTSHB_SERV,
            diaSemana: item.CTSHB_DDSEM,
            turno: item.CTSHB_TURNO,
            horaInicio: item.CTSHB_HHINI,
            horaFin: item.CTSHB_HHFIN,
            numPersonas: item.CTSHB_NUMPER
          });
        });
      }
      // Siempre devolver formato esperado por FirestoreCuadrantes
      return {
        errorCode: data.errorCode || '200',
        errorMessage: data.errorMessage || '',
        data: { total, items }
      };
    } else {
      throw new Error(`Respuesta inesperada de la API: ${response.status}`);
    }
    
  } catch (error) {
    console.error('❌ Error al obtener horarios operativos de contratos:', error);
    
    if (error.response) {
      console.error('📄 Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
      console.error('🔍 Error completo de respuesta:', JSON.stringify(error.response.data, null, 2));
      throw new Error(`Error ${error.response.status}: ${error.response.statusText} - ${error.response.data?.errorMessage || 'Sin mensaje'}`);
    } else if (error.request) {
      console.error('🌐 Error de red:', error.request);
      throw new Error('Error de conexión con la API de Freematica');
    } else {
      throw new Error(`Error: ${error.message}`);
    }
  }
};

/**
 * Busca servicios específicos en Freematica usando el endpoint contratos-servicios
 * @param {Array} localizaciones - Array de localizaciones en formato "contrato/servicio" (ej: ["12901/01", "14091/02"])
 * @param {string} empresa - Código de empresa (opcional, por defecto usa la configurada)
 * @returns {Promise<Object>} - Objeto con errorCode, errorMessage y data
 */
export const buscarServiciosPorLocalizacion = async (localizaciones = [], empresa = freematicaConfig.company) => {
  try {
    console.log('🔍 Iniciando búsqueda de servicios por localización...');
    console.log(`📋 Localizaciones a buscar: ${localizaciones.join(', ')}`);
    
    const headers = getHeaders(empresa);
    let url = `${freematicaConfig.apiUrl}/pvss/v1/contratos-servicios`;
    
    // Parsear las localizaciones para extraer contratos y servicios
    const localizacionesParsed = localizaciones.map(loc => {
      const [contrato, servicio] = loc.split('/');
      return { contrato, servicio, localizacion: loc };
    });
    
    // Si se proporcionan localizaciones específicas, construir query FIQL SOLO con contratos
    if (localizaciones && localizaciones.length > 0) {
      console.log(`📋 Filtrando por localizaciones: ${localizaciones.join(', ')}`);
      
      // ⚠️ IMPORTANTE: La API de Freematica NO acepta filtrar por CTRTS_COD
      // Solo filtramos por CONTRATO y luego filtramos por SERVICIO en JavaScript
      const contratosUnicos = [...new Set(localizacionesParsed.map(l => l.contrato))];
      const filtrosFIQL = contratosUnicos.map(contrato => `CTRTS_CTRT==${contrato}`);
      
      // Combinar todos los filtros con OR (coma en FIQL)
      const queryFIQL = filtrosFIQL.join(',');
      url += `?rquery=${encodeURIComponent(queryFIQL)}`;
      
      console.log(`🔍 Query FIQL construida (solo contratos): ${queryFIQL}`);
      console.log(`💡 Los servicios específicos se filtrarán después en JavaScript`);
    }
    
    console.log(`🌐 URL de petición: ${url}`);
    console.log('🔧 Headers que se envían:', JSON.stringify(headers, null, 2));
    
    const response = await axios.get(url, { headers });
    
    if (response.status === 200 && response.data) {
      const data = response.data;
      console.log(`✅ Servicios obtenidos de Freematica`);
      console.log(`📊 Total de registros en API: ${data.data?.total || 'N/A'}`);
      console.log(`📋 Items recibidos: ${data.data?.items?.length || 0}`);
      
      // Filtrar por servicio en JavaScript (ya que la API no lo acepta en FIQL)
      if (localizaciones && localizaciones.length > 0 && data.data?.items) {
        const serviciosBuscados = new Set(localizacionesParsed.map(l => l.servicio));
        const itemsFiltrados = data.data.items.filter(item => 
          serviciosBuscados.has(item.CTRTS_COD)
        );
        
        console.log(`🔍 Filtrado en JavaScript por servicios: ${[...serviciosBuscados].join(', ')}`);
        console.log(`📋 Items después de filtrar: ${itemsFiltrados.length}`);
        
        // Reemplazar los items con los filtrados
        data.data.items = itemsFiltrados;
        data.data.total = itemsFiltrados.length.toString();
      }
      
      // Log de información de los registros finales
      if (data.data?.items && data.data.items.length > 0) {
        console.log('📊 Servicios encontrados (después de filtrar):');
        data.data.items.forEach((item, index) => {
          console.log(`📋 Servicio ${index + 1}:`, {
            contrato: item.CTRTS_CTRT,
            codigo: item.CTRTS_COD,
            descripcion: item.CTRTS_DES,
            delegacion: item.CTRTS_DELEG,
            empresa: item.CTRTS_EMP,
            inspector: item.CTRTS_INSP,
            tipo: item.CTRTS_TIPO,
            fechaAlta: item.CTRTS_FECALTA,
            fechaFin: item.CTRTS_FECFIN,
            observaciones: item.CTRTS_OBS_PUESTO
          });
        });
      } else {
        console.warn('⚠️ No se encontraron servicios que coincidan con los criterios');
      }
      
      return data;
    } else {
      throw new Error(`Respuesta inesperada de la API: ${response.status}`);
    }
    
  } catch (error) {
    console.error('❌ Error al buscar servicios por localización:', error);
    
    if (error.response) {
      console.error('📄 Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
      console.error('🔍 Error completo de respuesta:', JSON.stringify(error.response.data, null, 2));
      throw new Error(`Error ${error.response.status}: ${error.response.statusText} - ${error.response.data?.errorMessage || 'Sin mensaje'}`);
    } else if (error.request) {
      console.error('🌐 Error de red:', error.request);
      throw new Error('Error de conexión con la API de Freematica');
    } else {
      throw new Error(`Error: ${error.message}`);
    }
  }
};

/**
 * Valida si el mes/año seleccionado está dentro del rango de fechas del contrato
 * @param {string} localizacion - Localización en formato "contrato/servicio" (ej: "14091/01")
 * @param {number} mes - Mes a validar (1-12)
 * @param {number} año - Año a validar (ej: 2025)
 * @param {string} empresa - Código de empresa (opcional, por defecto usa la configurada)
 * @returns {Promise<Object>} - Objeto con { esValido: boolean, mensaje: string, fechaAlta: string, fechaFin: string }
 */
export const validarFechasContrato = async (localizacion, mes, año, empresa = freematicaConfig.company) => {
  try {
    console.log(`📅 Validando fechas del contrato ${localizacion} para ${mes}/${año}`);
    
    // Obtener información del contrato desde Freematica
    const response = await buscarServiciosPorLocalizacion([localizacion], empresa);
    
    if (!response || response.errorCode !== "200" || !response.data?.items || response.data.items.length === 0) {
      return {
        esValido: false,
        mensaje: `No se encontró el contrato/servicio ${localizacion}`,
        fechaAlta: null,
        fechaFin: null
      };
    }
    
    const contrato = response.data.items[0];
    const fechaAltaStr = contrato.CTRTS_FECALTA;
    const fechaFinStr = contrato.CTRTS_FECFIN;
    
    console.log(`📋 Contrato encontrado:`, {
      contrato: contrato.CTRTS_CTRT,
      servicio: contrato.CTRTS_COD,
      descripcion: contrato.CTRTS_DES,
      fechaAlta: fechaAltaStr,
      fechaFin: fechaFinStr
    });
    
    // Si no hay fecha de alta, no podemos validar
    if (!fechaAltaStr) {
      console.warn('⚠️ El contrato no tiene fecha de alta definida');
      return {
        esValido: true, // Permitir si no hay fecha de alta
        mensaje: 'El contrato no tiene fecha de alta definida',
        fechaAlta: null,
        fechaFin: fechaFinStr
      };
    }
    
    // Convertir fechas de string a Date
    const fechaAlta = new Date(fechaAltaStr);
    const fechaFin = fechaFinStr ? new Date(fechaFinStr) : null;
    
    // Crear la fecha del mes/año seleccionado (primer día del mes)
    const fechaSeleccionada = new Date(año, mes - 1, 1);
    
    // Crear la fecha del último día del mes seleccionado
    const ultimoDiaMes = new Date(año, mes, 0);
    
    console.log(`🔍 Comparando fechas:`, {
      fechaAltaContrato: fechaAlta.toISOString(),
      fechaFinContrato: fechaFin ? fechaFin.toISOString() : 'Sin fecha fin',
      primerDiaMesSeleccionado: fechaSeleccionada.toISOString(),
      ultimoDiaMesSeleccionado: ultimoDiaMes.toISOString()
    });
    
    // Validar que al menos parte del mes esté dentro del rango del contrato
    // El mes es válido si:
    // 1. El último día del mes es >= fecha de alta Y
    // 2. (No hay fecha fin O el primer día del mes es <= fecha fin)
    
    const esValido = ultimoDiaMes >= fechaAlta && (!fechaFin || fechaSeleccionada <= fechaFin);
    
    const formatearFecha = (fecha) => {
      if (!fecha) return 'Sin fecha';
      const d = new Date(fecha);
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    };
    
    const nombresMeses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    
    if (!esValido) {
      let mensaje = `❌ No se puede cargar el cuadrante para ${nombresMeses[mes - 1]} ${año}.\n\n`;
      mensaje += `📋 Contrato/Servicio: ${contrato.CTRTS_CTRT}/${contrato.CTRTS_COD}\n`;
      mensaje += `📝 Descripción: ${contrato.CTRTS_DES}\n\n`;
      mensaje += `📅 Fecha de Alta: ${formatearFecha(fechaAltaStr)}\n`;
      mensaje += `📅 Fecha de Fin: ${formatearFecha(fechaFinStr)}\n\n`;
      
      if (ultimoDiaMes < fechaAlta) {
        mensaje += `⚠️ El mes seleccionado es anterior a la fecha de alta del contrato.`;
      } else if (fechaFin && fechaSeleccionada > fechaFin) {
        mensaje += `⚠️ El mes seleccionado es posterior a la fecha de finalización del contrato.`;
      }
      
      console.log(`❌ Validación fallida: ${mensaje}`);
      
      return {
        esValido: false,
        mensaje,
        fechaAlta: formatearFecha(fechaAltaStr),
        fechaFin: formatearFecha(fechaFinStr),
        contratoInfo: {
          contrato: contrato.CTRTS_CTRT,
          servicio: contrato.CTRTS_COD,
          descripcion: contrato.CTRTS_DES
        }
      };
    }
    
    console.log(`✅ Validación exitosa: El mes/año está dentro del rango del contrato`);
    
    return {
      esValido: true,
      mensaje: `✅ El contrato está activo para ${nombresMeses[mes - 1]} ${año}`,
      fechaAlta: formatearFecha(fechaAltaStr),
      fechaFin: formatearFecha(fechaFinStr),
      contratoInfo: {
        contrato: contrato.CTRTS_CTRT,
        servicio: contrato.CTRTS_COD,
        descripcion: contrato.CTRTS_DES
      }
    };
    
  } catch (error) {
    console.error('❌ Error al validar fechas del contrato:', error);
    return {
      esValido: false,
      mensaje: `Error al validar fechas del contrato: ${error.message}`,
      fechaAlta: null,
      fechaFin: null
    };
  }
};

/**
 * Obtiene los festivos de un calendario específico desde Freematica
 * @param {string} codigoCalendario - Código del calendario festivo (ej: "PLASEN")
 * @param {number} año - Año para filtrar (ej: 2025)
 * @param {string} empresa - Código de empresa (opcional, por defecto usa la configurada)
 * @returns {Promise<Object>} - Objeto con los festivos del calendario
 */
export const obtenerFestivosCalendario = async (codigoCalendario, año, empresa = freematicaConfig.company) => {
  try {
    console.log(`📅 Obteniendo festivos del calendario ${codigoCalendario} para el año ${año}`);
    
    const headers = getHeaders(empresa);
    const url = `${freematicaConfig.apiUrl}/pgrl/v2/calen-festivos`;
    
    // IMPORTANTE: No usamos filtro FIQL porque causa error 400 con algunos calendarios
    // En su lugar, obtenemos todos y filtramos en JavaScript
    console.log(`🔍 Obteniendo todos los calendarios (sin filtro FIQL)`);
    console.log(`🌐 URL de petición: ${url}`);
    
    const response = await axios.get(url, { headers });
    
    if (response.status === 200 && response.data) {
      const data = response.data;
      console.log(`✅ Respuesta obtenida exitosamente`);
      console.log(`📊 Total de calendarios disponibles: ${data.data?.items?.length || 0}`);
      
      // Buscar el calendario específico en la respuesta
      if (data.data?.items && data.data.items.length > 0) {
        const calendario = data.data.items.find(c => c.COD_CALEN_FESTIVOS === codigoCalendario);
        
        if (!calendario) {
          console.warn(`⚠️ No se encontró el calendario ${codigoCalendario} en la respuesta`);
          return {
            errorCode: "404",
            errorMessage: `No se encontró el calendario ${codigoCalendario}`,
            data: {
              codigoCalendario,
              descripcion: '',
              año,
              totalFestivos: 0,
              festivos: []
            }
          };
        }
        
        const todosLosFestivos = calendario.DETALLE || [];
        
        // Filtrar festivos por año en JavaScript
        const añoStr = año.toString();
        const festivos = todosLosFestivos.filter(f => f.COD_CALEN === añoStr);
        
        console.log(`📋 Calendario: ${calendario.COD_CALEN_FESTIVOS} - ${calendario.DESCRIPCION}`);
        console.log(`📅 Total festivos en todos los años: ${todosLosFestivos.length}`);
        console.log(`🎉 Total festivos filtrados para ${año}: ${festivos.length}`);
        
        // Formatear festivos para fácil uso
        const festivosFormateados = festivos.map(f => ({
          fecha: f.FECHA.split(' ')[0], // Solo la fecha sin hora
          descripcion: f.DESC_FEST_DET,
          tipo: f.TIPO_FEST || 'N',
          fechaCompleta: f.FECHA,
          codigoCalendario: f.COD_CALEN,
          codigoFestivo: f.COD_FEST_DET
        })).sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        
        return {
          errorCode: "200",
          errorMessage: "OK",
          data: {
            codigoCalendario: calendario.COD_CALEN_FESTIVOS,
            descripcion: calendario.DESCRIPCION,
            año: año,
            totalFestivos: festivosFormateados.length,
            festivos: festivosFormateados
          }
        };
      } else {
        console.warn(`⚠️ No se encontraron festivos para el calendario ${codigoCalendario} en ${año}`);
        return {
          errorCode: "404",
          errorMessage: `No se encontraron festivos para el calendario ${codigoCalendario}`,
          data: {
            codigoCalendario,
            descripcion: '',
            año,
            totalFestivos: 0,
            festivos: []
          }
        };
      }
    } else {
      throw new Error(`Respuesta inesperada de la API: ${response.status}`);
    }
    
  } catch (error) {
    console.error('❌ Error al obtener festivos del calendario:', error);
    
    if (error.response) {
      console.error('📄 Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
      throw new Error(`Error ${error.response.status}: ${error.response.statusText}`);
    } else if (error.request) {
      console.error('🌐 Error de red:', error.request);
      throw new Error('Error de conexión con la API de Freematica');
    } else {
      throw new Error(`Error: ${error.message}`);
    }
  }
};

/**
 * Obtiene el código de calendario festivo de un contrato
 * @param {string} localizacion - Localización en formato "contrato/servicio" (ej: "14091/01")
 * @param {string} empresa - Código de empresa (opcional, por defecto usa la configurada)
 * @returns {Promise<string|null>} - Código del calendario festivo (ej: "PLASEN") o null si no existe
 */
export const obtenerCodigoCalendarioContrato = async (localizacion, empresa = freematicaConfig.company) => {
  try {
    console.log(`🔍 Obteniendo código de calendario festivo para ${localizacion}`);
    
    // Obtener información del contrato
    const response = await buscarServiciosPorLocalizacion([localizacion], empresa);
    
    if (!response || response.errorCode !== "200" || !response.data?.items || response.data.items.length === 0) {
      console.warn(`⚠️ No se encontró el contrato/servicio ${localizacion}`);
      return null;
    }
    
    const contrato = response.data.items[0];
    const codigoCalendario = contrato.CTRTS_CALFEST;
    
    console.log(`📋 Contrato: ${contrato.CTRTS_CTRT}/${contrato.CTRTS_COD}`);
    console.log(`📅 Código calendario festivo: ${codigoCalendario || 'No definido'}`);
    
    return codigoCalendario || null;
    
  } catch (error) {
    console.error('❌ Error al obtener código de calendario del contrato:', error);
    return null;
  }
};

/**
 * Obtiene los festivos de un contrato específico para un año
 * @param {string} localizacion - Localización en formato "contrato/servicio" (ej: "14091/01")
 * @param {number} año - Año para filtrar (ej: 2025)
 * @param {string} empresa - Código de empresa (opcional, por defecto usa la configurada)
 * @returns {Promise<Object>} - Objeto con los festivos del contrato
 */
export const obtenerFestivosContrato = async (localizacion, año, empresa = freematicaConfig.company) => {
  try {
    console.log(`🎉 Obteniendo festivos para contrato ${localizacion} en año ${año}`);
    
    // 1. Obtener el código de calendario festivo del contrato
    const codigoCalendario = await obtenerCodigoCalendarioContrato(localizacion, empresa);
    
    if (!codigoCalendario) {
      return {
        errorCode: "404",
        errorMessage: `El contrato ${localizacion} no tiene calendario festivo definido`,
        data: {
          localizacion,
          codigoCalendario: null,
          año,
          totalFestivos: 0,
          festivos: []
        }
      };
    }
    
    // 2. Obtener los festivos del calendario
    const festivosData = await obtenerFestivosCalendario(codigoCalendario, año, empresa);
    
    // 3. Agregar información del contrato a la respuesta
    return {
      ...festivosData,
      data: {
        ...festivosData.data,
        localizacion
      }
    };
    
  } catch (error) {
    console.error('❌ Error al obtener festivos del contrato:', error);
    return {
      errorCode: "500",
      errorMessage: `Error al obtener festivos: ${error.message}`,
      data: {
        localizacion,
        año,
        totalFestivos: 0,
        festivos: []
      }
    };
  }
};

/**
 * Obtiene el tag/identificador de una persona por su código, seleccionando específicamente para empresa 0040
 * @param {string} codPersona - Código de la persona (vigilante.persona)
 * @param {string} empresaPreferida - Código de empresa preferida para filtrar (opcional)
 * @returns {Promise<string>} - Promesa con el tag de la persona
 */
export const obtenerTagPersona = async (codPersona, empresaPreferida = "0040") => {
  try {
    // Validación básica del formato del código
    if (!codPersona || typeof codPersona !== 'string' || codPersona.trim() === '') {
      console.error(`❌ Código de persona inválido o vacío: "${codPersona}"`);
      throw new Error(`Código de persona inválido o vacío: "${codPersona}"`);
    }
    
    // Si codPersona parece ser un ID de Firebase (largo y con caracteres especiales),
    // es probable que sea vigilante.id en lugar de vigilante.persona
    if (codPersona && codPersona.length > 20 && /[a-zA-Z0-9]{20,}/.test(codPersona)) {
      console.warn(`❌ Se pasó ID de Firebase como código de persona: ${codPersona}`);
      console.warn("Debe usar vigilante.persona en lugar de vigilante.id");
      throw new Error('Código de persona inválido (parece ID de Firebase)');
    }
    
    // Si el código es "SNP" o contiene palabras como "undefined" o "null", rechazarlo
    if (codPersona === 'SNP' || codPersona.includes('undefined') || codPersona.includes('null')) {
      console.error(`❌ Código de persona inválido: "${codPersona}"`);
      throw new Error(`Código de persona inválido: "${codPersona}"`);
    }
    
    const codPersonaStr = String(codPersona || '').trim();
    console.log(`Obteniendo tag para persona con código: "${codPersonaStr}"`);
    
    // IMPORTANTE: Esta API no necesita parámetro empresa, solo codPersona
    const response = await axios.get(
      `${freematicaConfig.apiUrl}/pers/v2/personal-identificacion`,
      {
        params: { codPersona: codPersonaStr }, // Solo codPersona, sin empresa
        headers: getHeaders() // Headers siempre con 0001
      }
    );

    // Verificar si la respuesta es correcta
    if (response.data && response.data.errorCode === '200') {
      const items = response.data.data?.items || [];
      
      if (items.length === 0) {
        console.error('❌ No se encontraron datos para la persona');
        throw new Error(`No se encontró tag para la persona con código: "${codPersonaStr}"`);
      }
      
      console.log(`Se encontraron ${items.length} identificadores para la persona ${codPersonaStr}`);
      
      // Mostrar todos los tags disponibles para depuración
      items.forEach((item, index) => {
        console.log(`Tag #${index+1}:`, {
          empresa: item.PIDEN_EMP,
          delegacion: item.PIDEN_DELEG,
          persona: item.PIDEN_PERSONA,
          tag: item.PIDEN_TAG
        });
      });
      
      // PRIMERO: Usar la empresa preferida (ej. la del servicio al que se asigna)
      let selectedItem = items.find(item => item.PIDEN_EMP === empresaPreferida);
      
      // Si no se encuentra en la empresa preferida, buscar en otras en orden de prioridad
      if (!selectedItem) {
        const empresasPrioritarias = [empresaPreferida, "0040", "0020", "0005", "0006", "0001"].filter((e, i, arr) => arr.indexOf(e) === i);
        
        for (const emp of empresasPrioritarias) {
          const match = items.find(item => item.PIDEN_EMP === emp);
          if (match) {
            console.log(`⚠️ No se encontró tag para empresa 0040, usando empresa alternativa: ${emp}`);
            selectedItem = match;
            break;
          }
        }
      }
      
      // Si aún no tenemos un item seleccionado, usar el primero como fallback
      if (!selectedItem && items.length > 0) {
        console.warn('⚠️ No se encontró tag en ninguna empresa prioritaria, usando el primer tag disponible');
        selectedItem = items[0];
      }
      
      if (!selectedItem) {
        throw new Error('No se pudo seleccionar un tag válido para la persona');
      }
      
      // Mostrar información del item seleccionado
      console.log("🎯 Tag seleccionado:", {
        empresa: selectedItem.PIDEN_EMP,
        delegacion: selectedItem.PIDEN_DELEG,
        persona: selectedItem.PIDEN_PERSONA,
        tag: selectedItem.PIDEN_TAG
      });
      
      const tag = selectedItem.PIDEN_TAG;
      console.log(`✅ Tag obtenido correctamente: "${tag}"`);
      return tag;
    } else {
      console.error('❌ Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener tag de persona');
    }
  } catch (error) {
    console.error(`❌ Error al obtener tag de persona "${codPersona}":`, error.message);
    throw error;
  }
};


/**
 * Obtiene los identificadores de servicio para una empresa
 * @returns {Promise} - Promesa con los datos de identificadores
 */
export const obtenerIdentificadoresServicio = async () => {
  try {
    // IMPORTANTE: Siempre usar empresa '0020' para identificadores
    const empresaStr = '0020';
    
    console.log(`Obteniendo identificadores para empresa: ${empresaStr}`);
    
    const response = await axios.get(
      `${freematicaConfig.apiUrl}/pemf/v2/identificadores-servicio`, 
      {
        params: { empresa: empresaStr }, // Siempre empresa 0020
        headers: getHeaders()  // Headers siempre con 0001
      }
    );

    // Verificar si la respuesta es correcta
    if (response.data && response.data.errorCode === '200') {
      const items = response.data.data.items || [];
      console.log(`Se encontraron ${items.length} identificadores para empresa ${empresaStr}`);
      
      if (items.length > 0) {
        // Mostrar ejemplos para debug
        console.log("Primeros 2 identificadores para referencia:", 
          items.slice(0, 2).map(item => ({
            FOPEST_EMP: item.FOPEST_EMP,
            FOPEST_DELEG: item.FOPEST_DELEG,
            FOPEST_CTRT: item.FOPEST_CTRT,
            FOPEST_SERV: item.FOPEST_SERV,
            FOPEST_TEL: item.FOPEST_TEL,
            idReg: item.idReg
          }))
        );
      } else {
        console.warn(`No se encontraron identificadores para la empresa ${empresaStr}`);
      }
      
      return items;
    } else {
      console.error('Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || `Error al obtener identificadores de servicio para empresa ${empresaStr}`);
    }
  } catch (error) {
    console.error(`Error al obtener identificadores de servicio:`, error);
    throw error;
  }
};

/**
 * Extrae contrato y delegación del campo localización
 * @param {string} localizacion - Valor del campo localización (ej: "13853/06")
 * @returns {Object} - { contrato, delegacion, servicio }
 */
export const extraerDatosLocalizacion = (localizacion) => {
  if (!localizacion) {
    throw new Error('El valor de localización es inválido');
  }
  
  console.log(`Extrayendo datos de localización: "${localizacion}"`);
  
  // Limpiar espacios y asegurar formato
  const localizacionLimpia = localizacion.trim();
  
  // El formato puede ser "CONTRATO/SERVICIO" (XXXX/XX) o "EMPRESA/DELEGACION/CONTRATO/SERVICIO"
  const partes = localizacionLimpia.split('/');
  
  if (partes.length < 2) {
    throw new Error(`El formato de localización debe ser "CONTRATO/SERVICIO" (XXXX/XX) o "EMPRESA/DELEGACION/CONTRATO/SERVICIO". Valor recibido: "${localizacion}"`);
  }
  
  let empresa, delegacion, contrato, servicio;
  
  if (partes.length === 2) {
    // Formato: "CONTRATO/SERVICIO"
    contrato = partes[0].trim();
    servicio = partes[1].trim();
    empresa = '0001'; // Default
    delegacion = '06'; // Default
  } else if (partes.length === 4) {
    // Formato: "EMPRESA/DELEGACION/CONTRATO/SERVICIO"
    empresa = partes[0].trim();
    delegacion = partes[1].trim();
    contrato = partes[2].trim();
    servicio = partes[3].trim();
  } else {
    throw new Error(`Formato de localización no reconocido. Debe ser "CONTRATO/SERVICIO" o "EMPRESA/DELEGACION/CONTRATO/SERVICIO". Valor recibido: "${localizacion}"`);
  }
  
  const resultado = {
    empresa: empresa,
    delegacion: delegacion,
    contrato: contrato,
    servicio: servicio
  };
  
  console.log("Datos extraídos:", resultado);
  return resultado;
};

/**
 * Busca el servicio en Freematica basado en los criterios proporcionados
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} codigoServicio - Código de servicio (opcional)
 * @returns {Promise<{idReg: string, serviceTag: string, empresa: string}>} - Información del servicio
 */
export const buscarServicioFreematica = async (empresa, delegacion, contrato, codigoServicio = "01") => {
  try {
    // Asegurar que todos los parámetros son strings y están limpios
    const empresaStr = String(empresa || '0001').trim();
    const delegacionStr = String(delegacion || '').trim();
    const contratoStr = String(contrato || '').trim();
    const servicioStr = String(codigoServicio || '01').trim();
    
    // Validar que tenemos datos suficientes
    if (!contratoStr) {
      throw new Error("El código de contrato es obligatorio para buscar un servicio");
    }
    
    console.log(`Buscando servicio con parámetros: Empresa=${empresaStr}, Delegación=${delegacionStr}, Contrato=${contratoStr}, Servicio=${servicioStr}`);
    
    // 1. Obtener todos los identificadores de servicio para la empresa
    const identificadores = await obtenerIdentificadoresServicio(empresaStr);
    
    if (identificadores.length === 0) {
      throw new Error(`No se encontraron identificadores para la empresa ${empresaStr}`);
    }
    
    console.log(`Buscando identificador en ${identificadores.length} resultados...`);
    
    // Debug: mostrar algunos identificadores para verificar estructura
    console.log("Muestra de identificadores para referencia:");
    identificadores.slice(0, 3).forEach((item, index) => {
      console.log(`[${index}]: DELEG=${item.FOPEST_DELEG}, CTRT=${item.FOPEST_CTRT}, SERV=${item.FOPEST_SERV}`);
    });
    
    // Estrategia de búsqueda en orden de prioridad
    let identificador = null;
    
    // 1. Búsqueda exacta con todos los parámetros
    if (delegacionStr && contratoStr && servicioStr) {
      identificador = identificadores.find(item => 
        item.FOPEST_DELEG === delegacionStr &&
        item.FOPEST_CTRT === contratoStr &&
        item.FOPEST_SERV === servicioStr
      );
      if (identificador) {
        console.log("✅ Encontrado con búsqueda exacta completa");
      }
    }
    
    // 2. Búsqueda con delegación y contrato (sin servicio)
    if (!identificador && delegacionStr && contratoStr) {
      console.log("🔍 Intentando búsqueda con delegación y contrato (sin servicio)");
      identificador = identificadores.find(item => 
        item.FOPEST_DELEG === delegacionStr &&
        item.FOPEST_CTRT === contratoStr
      );
      if (identificador) {
        console.log("✅ Encontrado con delegación y contrato");
      }
    }
    
    // 3. Búsqueda solo con contrato
    if (!identificador && contratoStr) {
      console.log("🔍 Intentando búsqueda solo con contrato");
      identificador = identificadores.find(item => 
        item.FOPEST_CTRT === contratoStr
      );
      if (identificador) {
        console.log("✅ Encontrado solo con contrato");
      }
    }
    
    // 4. Búsqueda con contrato y servicio (sin delegación)
    if (!identificador && contratoStr && servicioStr) {
      console.log("🔍 Intentando búsqueda con contrato y servicio (sin delegación)");
      identificador = identificadores.find(item => 
        item.FOPEST_CTRT === contratoStr &&
        item.FOPEST_SERV === servicioStr
      );
      if (identificador) {
        console.log("✅ Encontrado con contrato y servicio");
      }
    }
    
    // 5. Si no se encuentra, buscar el primer servicio disponible para el contrato
    if (!identificador && contratoStr) {
      console.log("🔍 Buscando cualquier servicio disponible para el contrato");
      const serviciosDisponibles = identificadores.filter(item => 
        item.FOPEST_CTRT === contratoStr
      );
      if (serviciosDisponibles.length > 0) {
        identificador = serviciosDisponibles[0];
        console.log("✅ Usando primer servicio disponible para el contrato");
      }
    }

    if (!identificador) {
      // Mostrar valores disponibles para diagnóstico
      const delegacionesDisponibles = [...new Set(identificadores.map(item => item.FOPEST_DELEG))];
      const contratosDisponibles = [...new Set(identificadores.map(item => item.FOPEST_CTRT))];
      const serviciosDisponibles = [...new Set(identificadores.map(item => item.FOPEST_SERV))];
      
      console.error("Valores de delegación disponibles:", delegacionesDisponibles);
      console.error("Valores de contrato disponibles:", contratosDisponibles);
      console.error("Valores de servicio disponibles:", serviciosDisponibles);
      
      // Sugerir valores alternativos
      if (contratosDisponibles.includes(contratoStr)) {
        console.log(`✅ El contrato ${contratoStr} existe, pero no con la delegación/servicio especificados`);
        const serviciosParaContrato = identificadores.filter(item => item.FOPEST_CTRT === contratoStr);
        console.log("Servicios disponibles para este contrato:", serviciosParaContrato.map(s => `${s.FOPEST_DELEG}/${s.FOPEST_SERV}`));
      }
      
      throw new Error(`No se encontró un identificador de servicio con los criterios especificados: 
        Delegación=${delegacionStr}, Contrato=${contratoStr}, Código=${servicioStr}
        
        Sugerencias:
        - Verificar que la delegación sea una de: ${delegacionesDisponibles.join(', ')}
        - Verificar que el contrato sea uno de: ${contratosDisponibles.join(', ')}
        - Verificar que el servicio sea uno de: ${serviciosDisponibles.join(', ')}`);
    }
    
    // 3. Extraer la información necesaria para crear marcajes
    const idReg = identificador.idReg;          // Para la URL del endpoint
    const serviceTag = identificador.FOPEST_TEL; // Para el cuerpo del marcaje
    
    console.log(`✅ Identificador encontrado: idReg=${idReg}, serviceTag=${serviceTag}, empresa=${empresaStr}`);
    console.log(`   Delegación: ${identificador.FOPEST_DELEG}, Contrato: ${identificador.FOPEST_CTRT}, Servicio: ${identificador.FOPEST_SERV}`);
    
    return {
      idReg: idReg,           // Usado para construir la URL
      serviceTag: serviceTag,  // Usado en el cuerpo del marcaje (FOPEST_TEL)
      empresa: empresaStr,     // Guardamos la empresa para futuras llamadas
      delegacion: identificador.FOPEST_DELEG,  // Delegación encontrada
      contrato: identificador.FOPEST_CTRT,     // Contrato encontrado
      servicio: identificador.FOPEST_SERV      // Servicio encontrado
    };
  } catch (error) {
    console.error('Error al buscar servicio en Freematica:', error);
    throw error;
  }
};

/**
 * Crea un marcaje para un servicio específico
 * @param {Object} servicioInfo - Objeto con información del servicio {idReg, serviceTag, empresa}
 * @param {Array} datosMarcajes - Array con los datos de los marcajes a crear
 * @returns {Promise} - Promesa con la respuesta de la creación
 */
export const crearMarcaje = async (servicioInfo, datosMarcajes) => {
  try {
    // Verificar que el identificador existe
    if (!servicioInfo || !servicioInfo.idReg) {
      throw new Error('El identificador del servicio (idReg) es requerido');
    }
    
    if (!servicioInfo.serviceTag) {
      throw new Error('El identificador de servicio (serviceTag/FOPEST_TEL) es requerido');
    }

    // Construir el endpoint para crear el marcaje
    const endpoint = `${freematicaConfig.apiUrl}/pemf/v1/servicio/${servicioInfo.idReg}/crear-marcaje`;

    console.log('Enviando marcaje a:', endpoint);
    console.log('Usando serviceTag (FOPEST_TEL):', servicioInfo.serviceTag);
    
    // Actualizar el serviceTag en los datos del marcaje
    const marcajesActualizados = datosMarcajes.map(marcaje => ({
      ...marcaje,
      serviceTag: servicioInfo.serviceTag
    }));
    
    // Verificar que los marcajes incluyen un personTag válido
    for (const marcaje of marcajesActualizados) {
      if (!marcaje.personTag || marcaje.personTag.includes('undefined') || marcaje.personTag.includes('SNP')) {
        console.error('Error: El tag de persona no es válido:', marcaje.personTag);
        throw new Error(`El tag de persona "${marcaje.personTag}" no es válido`);
      }
    }
    
    console.log('Datos de marcaje a enviar:', JSON.stringify(marcajesActualizados, null, 2));

    // Realizar la petición POST usando la empresa correcta en los headers
    const response = await axios.post(
      endpoint,
      marcajesActualizados,
      { 
        headers: getHeaders(servicioInfo.empresa),
        timeout: 10000
      }
    );

    // Verificar si la respuesta es correcta
    if (response.data && response.data.errorCode === '200') {
      return response.data;
    } else {
      console.error('Error en la respuesta de la API:', response.data);
      throw new Error(response.data.errorMessage || 'Error al crear marcaje');
    }
  } catch (error) {
    if (error.response) {
      console.error('Error en la respuesta:', {
        status: error.response.status,
        data: error.response.data
      });
      throw new Error(`Error en la API (${error.response.status}): ${JSON.stringify(error.response.data)}`);
    } else if (error.request) {
      console.error('No se recibió respuesta:', error.request);
      throw new Error('No se recibió respuesta del servidor');
    } else {
      console.error('Error en la configuración de la petición:', error.message);
      throw error;
    }
  }
};


/**
 * Crea un marcaje de turno (entrada y salida)
 * @param {Object} servicioInfo - Objeto con información del servicio {idReg, serviceTag, empresa}
 * @param {string} personTag - ID de la persona
 * @param {string} fecha - Fecha en formato YYYY-MM-DD
 * @param {string} horaEntrada - Hora de entrada en formato HH:MM
 * @param {string} horaSalida - Hora de salida en formato HH:MM
 * @param {string} observaciones - Observaciones para el marcaje
 * @returns {Promise} - Promesa con la respuesta de la creación
 */
export const crearMarcajeTurno = async (servicioInfo, personTag, fecha, horaEntrada, horaSalida, observaciones = "") => {
  try {
    // Verificar que tenemos la información necesaria
    if (!servicioInfo || !servicioInfo.idReg || !servicioInfo.serviceTag) {
      throw new Error('La información del servicio es incompleta o inválida');
    }
    
    if (!personTag || personTag.includes('undefined') || personTag.includes('SNP') || personTag.includes('null')) {
      throw new Error(`El tag de persona "${personTag}" no es válido`);
    }
    
    console.log(`Creando marcaje de turno: Persona=${personTag}, Fecha=${fecha}, Entrada=${horaEntrada}, Salida=${horaSalida}`);
    
    // Formatear fechas para la API en formato ISO (YYYY-MM-DDTHH:MM:SS)
    const fechaEntrada = `${fecha}T${horaEntrada}:00`;
    
    // Calcular la fecha de salida
    let fechaSalida;
    if (horaSalida < horaEntrada) {
      const [year, month, day] = fecha.split('-');
      const nextDay = new Date(year, month - 1, parseInt(day) + 1);
      // Formatear fecha sin usar toISOString() para evitar problemas de zona horaria
      const nextDayFormatted = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
      fechaSalida = `${nextDayFormatted}T${horaSalida}:00`;
    } else {
      fechaSalida = `${fecha}T${horaSalida}:00`;
    }
    
    // Headers siempre 0001 (auth). La empresa del servicio se usa en filters/params si aplica.
    
    // 1. Crear marcaje de entrada
    const marcajeEntrada = [{
      "idReg": "",
      "date": fechaEntrada,
      "trackType": "ENT",
      "device": "0",
      "serviceTag": servicioInfo.serviceTag, // Usar el serviceTag directamente
      "latitude": "",
      "longitude": "",
      "incidenceDescription": observaciones,
      "incidence": "",
      "personTag": personTag,
      "positionOrigin": "",
      "positionPrecision": "",
      "photo": "",
      "appVersion": "",
      "deviceVersion": "",
      "deviceLang": "",
      "urlCountry": ""
    }];
    
    console.log('Enviando marcaje de entrada:', JSON.stringify(marcajeEntrada[0]));
    
    // Construir el endpoint para crear el marcaje
    const endpoint = `${freematicaConfig.apiUrl}/pemf/v1/servicio/${servicioInfo.idReg}/crear-marcaje`;
    console.log('Enviando marcaje a:', endpoint);
    
    const headersSeguro = {
      'x-auth-token': freematicaConfig.token,
      'x-auth-app': freematicaConfig.appId,
      'x-auth-organization': freematicaConfig.organization,
      'x-auth-company': '0001',
      'x-auth-session': freematicaConfig.token,
      'Content-Type': 'application/json; charset=utf-8'
    };
    
    const responseEntrada = await axios.post(
      endpoint,
      marcajeEntrada,
      { 
        headers: headersSeguro,
        timeout: 10000
      }
    );
    
    // Verificar respuesta de entrada
    if (responseEntrada.data && responseEntrada.data.errorCode === '200') {
      console.log('Resultado marcaje entrada:', responseEntrada.data);
      const respuestaEntrada = responseEntrada.data;
      
      let respuestaSalida = null;
      
      // 2. Crear marcaje de salida si tiene hora de salida
      if (horaSalida) {
        const marcajeSalida = [{
          "idReg": "",
          "date": fechaSalida,
          "trackType": "SAL",
          "device": "0",
          "serviceTag": servicioInfo.serviceTag, // Usar el serviceTag directamente
          "latitude": "",
          "longitude": "",
          "incidenceDescription": observaciones,
          "incidence": "",
          "personTag": personTag,
          "positionOrigin": "",
          "positionPrecision": "",
          "photo": "",
          "appVersion": "",
          "deviceVersion": "",
          "deviceLang": "",
          "urlCountry": ""
        }];
        
        console.log('Enviando marcaje de salida:', JSON.stringify(marcajeSalida[0]));
        const responseSalida = await axios.post(
          endpoint,
          marcajeSalida,
          { 
            headers: headersSeguro,
            timeout: 10000
          }
        );
        
        if (responseSalida.data && responseSalida.data.errorCode === '200') {
          respuestaSalida = responseSalida.data;
          console.log('Resultado marcaje salida:', respuestaSalida);
        } else {
          console.error('Error en la respuesta de salida:', responseSalida.data);
          throw new Error(responseSalida.data.errorMessage || 'Error al crear marcaje de salida');
        }
      }
      
      return {
        entrada: respuestaEntrada,
        salida: respuestaSalida
      };
    } else {
      console.error('Error en la respuesta de entrada:', responseEntrada.data);
      const msg = responseEntrada.data?.errorMessage || responseEntrada.data?.data || 'Error al crear marcaje de entrada';
      const msgStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      if (msgStr.toLowerCase().includes('personal sin contratacion') || msgStr.toLowerCase().includes('personal sin contratación')) {
        throw new Error(
          'Personal sin contratación: El vigilante no tiene un registro de contratación activo en Freematica para esta empresa/servicio. ' +
          'Debe dar de alta la contratación del trabajador en Freematica (módulo de personal) y asignarlo al contrato/servicio correspondiente.'
        );
      }
      throw new Error(msgStr);
    }
  } catch (error) {
    console.error('Error al crear marcajes:', error);
    
    if (error.response) {
      const data = error.response.data;
      const msg = data?.errorMessage || data?.data || JSON.stringify(data);
      const msgStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      if (msgStr.toLowerCase().includes('personal sin contratacion') || msgStr.toLowerCase().includes('personal sin contratación')) {
        throw new Error(
          'Personal sin contratación: El vigilante no tiene un registro de contratación activo en Freematica para esta empresa/servicio. ' +
          'Debe dar de alta la contratación del trabajador en Freematica (módulo de personal) y asignarlo al contrato/servicio correspondiente.'
        );
      }
      throw new Error(`Error en la API (${error.response.status}): ${msgStr}`);
    }
    
    throw error;
  }
};

/**
 * Crea marcajes de turno (entrada y salida) en lote para múltiples turnos
 * @param {Object} servicioInfo - Objeto con información del servicio {idReg, serviceTag, empresa}
 * @param {Array} turnosData - Array de objetos con los datos de cada turno a crear
 *                            [{personTag, fecha, horaEntrada, horaSalida, observaciones}]
 * @returns {Promise} - Promesa con la respuesta de la creación
 */
export const crearMarcajesTurnoLote = async (servicioInfo, turnosData) => {
  try {
    // Verificar que tenemos la información necesaria
    if (!servicioInfo || !servicioInfo.idReg || !servicioInfo.serviceTag) {
      throw new Error('La información del servicio es incompleta o inválida');
    }
    
    if (!Array.isArray(turnosData) || turnosData.length === 0) {
      throw new Error('No se proporcionaron datos de turnos para crear marcajes');
    }
    
    console.log(`Creando marcaje de turnos en lote: ${turnosData.length} turnos`);
    
    // Usar la empresa del servicio para que coincida con la contratación del personal
    const empresaMarcaje = servicioInfo.empresa || "0001";
    
    // Construir el endpoint para crear el marcaje
    const endpoint = `${freematicaConfig.apiUrl}/pemf/v1/servicio/${servicioInfo.idReg}/crear-marcaje`;
    console.log('Enviando marcajes a:', endpoint);
    
    const headersSeguro = {
      'x-auth-token': freematicaConfig.token,
      'x-auth-app': freematicaConfig.appId,
      'x-auth-organization': freematicaConfig.organization,
      'x-auth-company': '0001',
      'x-auth-session': freematicaConfig.token,
      'Content-Type': 'application/json; charset=utf-8'
    };
    
    // Resultados
    const resultados = {
      entradas: [],
      salidas: [],
      totalProcesados: 0,
      errores: []
    };
    
    // CAMBIO CLAVE: Procesar los turnos uno por uno, pero enviando cada entrada/salida como par
    for (const turno of turnosData) {
      const { personTag, fecha, horaEntrada, horaSalida, observaciones = "" } = turno;
      
      if (!personTag || personTag.includes('undefined') || personTag.includes('SNP') || personTag.includes('null')) {
        console.warn(`Omitiendo turno con tag de persona inválido: "${personTag}"`);
        resultados.errores.push(`Tag de persona inválido para fecha ${fecha}: ${personTag}`);
        continue;
      }
      
      try {
        // Formatear fechas para la API en formato ISO (YYYY-MM-DDTHH:MM:SS)
        const fechaEntrada = `${fecha}T${horaEntrada}:00`;
        
        // Calcular la fecha de salida
        let fechaSalida;
        if (horaSalida < horaEntrada) {
          const [year, month, day] = fecha.split('-');
          const nextDay = new Date(year, month - 1, parseInt(day) + 1);
          // Formatear fecha sin usar toISOString() para evitar problemas de zona horaria
          const nextDayFormatted = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
          fechaSalida = `${nextDayFormatted}T${horaSalida}:00`;
        } else {
          fechaSalida = `${fecha}T${horaSalida}:00`;
        }
        
        // 1. Crear y enviar marcaje de ENTRADA para esta fecha
        const marcajeEntrada = [{
          "idReg": "",
          "date": fechaEntrada,
          "trackType": "ENT",
          "device": "0",
          "serviceTag": servicioInfo.serviceTag,
          "latitude": "",
          "longitude": "",
          "incidenceDescription": observaciones,
          "incidence": "",
          "personTag": personTag,
          "positionOrigin": "",
          "positionPrecision": "",
          "photo": "",
          "appVersion": "",
          "deviceVersion": "",
          "deviceLang": "",
          "urlCountry": ""
        }];
        
        console.log(`Enviando marcaje de entrada para fecha ${fecha}:`, JSON.stringify(marcajeEntrada[0]));
        
        const responseEntrada = await axios.post(
          endpoint,
          marcajeEntrada,
          { 
            headers: headersSeguro,
            timeout: 10000
          }
        );
        
        if (responseEntrada.data && responseEntrada.data.errorCode === '200') {
          resultados.entradas.push({
            fecha,
            respuesta: responseEntrada.data
          });
          console.log(`✅ Marcaje de entrada creado correctamente para fecha ${fecha}`);
        } else {
          console.error(`Error en marcaje de entrada para fecha ${fecha}:`, responseEntrada.data);
          resultados.errores.push(`Error en marcaje de entrada para fecha ${fecha}: ${responseEntrada.data.errorMessage || 'Error desconocido'}`);
          // Continuamos con la siguiente fecha aunque falle esta
          continue;
        }
        
        // 2. Crear y enviar marcaje de SALIDA para esta fecha (si hay hora de salida)
        if (horaSalida) {
          const marcajeSalida = [{
            "idReg": "",
            "date": fechaSalida,
            "trackType": "SAL",
            "device": "0",
            "serviceTag": servicioInfo.serviceTag,
            "latitude": "",
            "longitude": "",
            "incidenceDescription": observaciones,
            "incidence": "",
            "personTag": personTag,
            "positionOrigin": "",
            "positionPrecision": "",
            "photo": "",
            "appVersion": "",
            "deviceVersion": "",
            "deviceLang": "",
            "urlCountry": ""
          }];
          
          console.log(`Enviando marcaje de salida para fecha ${fecha}:`, JSON.stringify(marcajeSalida[0]));
          
          const responseSalida = await axios.post(
            endpoint,
            marcajeSalida,
            { 
              headers: headersSeguro,
              timeout: 10000
            }
          );
          
          if (responseSalida.data && responseSalida.data.errorCode === '200') {
            resultados.salidas.push({
              fecha,
              respuesta: responseSalida.data
            });
            console.log(`✅ Marcaje de salida creado correctamente para fecha ${fecha}`);
          } else {
            console.error(`Error en marcaje de salida para fecha ${fecha}:`, responseSalida.data);
            resultados.errores.push(`Error en marcaje de salida para fecha ${fecha}: ${responseSalida.data.errorMessage || 'Error desconocido'}`);
          }
        }
        
        resultados.totalProcesados++;
        
      } catch (errorTurno) {
        console.error(`Error procesando turno para fecha ${fecha}:`, errorTurno);
        resultados.errores.push(`Error procesando turno para fecha ${fecha}: ${errorTurno.message}`);
      }
      
      // Pequeña pausa entre cada turno para evitar sobrecarga
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    return {
      entradasCreadas: resultados.entradas.length,
      salidasCreadas: resultados.salidas.length,
      totalProcesados: resultados.totalProcesados,
      totalErrores: resultados.errores.length,
      errores: resultados.errores
    };
    
  } catch (error) {
    console.error('Error al crear marcajes en lote:', error);
    
    if (error.response) {
      console.error('Error en la respuesta:', {
        status: error.response.status,
        data: error.response.data
      });
      throw new Error(`Error en la API (${error.response.status}): ${JSON.stringify(error.response.data)}`);
    }
    
    throw error;
  }
};

/**
 * Codifica un ID de cuadrante en base64
 * @param {string} idReg - ID del cuadrante en formato: Empresa__Delegación__Calendario__Mes__Contrato__Servicio
 * @returns {string} - ID codificado en base64
 */
export const codificarIdReg = (idReg) => {
  try {
    return btoa(idReg);
  } catch (error) {
    console.error('Error al codificar ID:', error);
    throw new Error('Error al codificar el ID del cuadrante');
  }
};

/**
 * Decodifica un ID de cuadrante desde base64
 * @param {string} idRegCodificado - ID codificado en base64
 * @returns {string} - ID decodificado
 */
export const decodificarIdReg = (idRegCodificado) => {
  try {
    return atob(idRegCodificado);
  } catch (error) {
    console.error('Error al decodificar ID:', error);
    throw new Error('Error al decodificar el ID del cuadrante');
  }
};

/**
 * Construye el ID del cuadrante en formato requerido
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} calendario - Año del calendario
 * @param {string} mes - Mes del calendario
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @returns {string} - ID del cuadrante codificado en base64
 */
export const construirIdReg = (empresa, delegacion, calendario, mes, contrato, servicio) => {
  const idReg = `${empresa}__${delegacion}__${calendario}__${mes}__${contrato}__${servicio}`;
  return codificarIdReg(idReg);
};

/**
 * Actualiza un cuadrante a través de la API
 * @param {string} idReg - ID del cuadrante codificado en base64
 * @param {Array} turnosData - Array con los datos de los turnos a actualizar
 * @returns {Promise} - Promesa con la respuesta de la actualización
 */
export const actualizarCuadrante = async (idReg, turnosData) => {
  try {
    // Validar que el ID del cuadrante existe
    if (!idReg) {
      throw new Error('El ID del cuadrante es requerido');
    }

    // Validar que los datos de turnos existen
    if (!Array.isArray(turnosData) || turnosData.length === 0) {
      throw new Error('Los datos de turnos son requeridos y deben ser un array no vacío');
    }

    // Construir el endpoint para actualizar el cuadrante
    const endpoint = `${freematicaConfig.apiUrl}/pvss/v2/cuadrante/${idReg}`;

    console.log('Actualizando cuadrante en:', endpoint);
    console.log('Datos de turnos a enviar:', JSON.stringify(turnosData, null, 2));

    // Realizar la petición PUT
    const response = await axios.put(
      endpoint,
      turnosData,
      { 
        headers: getHeaders(),
        timeout: 30000 // Timeout más largo para operaciones de cuadrantes
      }
    );

    // Verificar si la respuesta es correcta (errorCode 200 puede incluir errores en data)
    if (response.data && response.data.errorCode === '200') {
      const mensaje = response.data.data || '';
      if (mensaje.includes('Cuadrante inexistente')) {
        throw new Error('El cuadrante no existe en Freematica. Debe crearse primero.');
      }
      if (mensaje.includes('Personal no vinculado al Servicio')) {
        throw new Error('El personal no está vinculado al servicio en Freematica');
      }
      if (mensaje.includes('Cuadrante NO Actualizado') || (mensaje.includes('Proceso Cancelado') && !mensaje.includes('Actualizado con éxito'))) {
        throw new Error(`Freematica rechazó la actualización: ${mensaje}`);
      }
      console.log('Cuadrante actualizado correctamente:', response.data);
      return response.data;
    } else {
      console.error('Error en la respuesta de la API:', response.data);
      throw new Error(response.data.errorMessage || 'Error al actualizar cuadrante');
    }
  } catch (error) {
    if (error.response) {
      console.error('Error en la respuesta:', {
        status: error.response.status,
        data: error.response.data
      });
      throw new Error(`Error en la API (${error.response.status}): ${JSON.stringify(error.response.data)}`);
    } else if (error.request) {
      console.error('No se recibió respuesta:', error.request);
      throw new Error('No se recibió respuesta del servidor');
    } else {
      console.error('Error en la configuración de la petición:', error.message);
      throw error;
    }
  }
};

/**
 * Convierte los datos de turnos de Firestore al formato requerido por la API
 * @param {Array} turnosFirestore - Array de turnos de Firestore
 * @param {Object} detallesServicio - Detalles del servicio
 * @returns {Promise<Array>} - Array de turnos en formato de la API
 */
export const convertirTurnosParaAPI = async (turnosFirestore, detallesServicio) => {
  try {
    const turnosAPI = [];
    
    // Agrupar turnos por persona
    const turnosPorPersona = {};
    
    for (const turno of turnosFirestore) {
      const codigoPersona = turno.vigilante; // Usar el código de persona
      
      if (!codigoPersona) {
        console.warn('Turno sin código de persona, omitiendo:', turno);
        continue;
      }
      
      if (!turnosPorPersona[codigoPersona]) {
        turnosPorPersona[codigoPersona] = [];
      }
      
      turnosPorPersona[codigoPersona].push(turno);
    }
    
    // Extraer empresa del servicio para obtener el tag correcto
    const datosLocalizacion = extraerDatosLocalizacion(detallesServicio.localizacion);
    const empresaServicio = datosLocalizacion.empresa || detallesServicio.empresa || "0001";
    
    // Convertir cada grupo de turnos por persona (cuadt_turnoper = 1, 2, 3... por persona)
    let turnoOperIndex = 0;
    for (const [codigoPersona, turnos] of Object.entries(turnosPorPersona)) {
      turnoOperIndex++;
      // Obtener el tag de la persona para la empresa del servicio
      let personTag;
      try {
        personTag = await obtenerTagPersona(codigoPersona, empresaServicio);
      } catch (error) {
        console.warn(`No se pudo obtener tag para persona ${codigoPersona}:`, error.message);
        turnoOperIndex--;
        continue; // Omitir esta persona si no se puede obtener su tag
      }
      
      // Crear el objeto de turno para la API (cuadt_deleg_perso = delegación, no servicio)
      const turnoAPI = {
        cuadt_turnoper: turnoOperIndex,
        cuadt_persona: codigoPersona,
        cuadt_emp_perso: empresaServicio,
        cuadt_deleg_perso: datosLocalizacion.delegacion || '06',
        Dias: []
      };
      
      // Convertir cada día del turno
      for (const turno of turnos) {
        const fecha = new Date(turno.fecha);
        const dia = fecha.getDate();
        
        // Calcular horas totales
        const horaInicio = turno.inicioTurno;
        const horaFin = turno.finTurno;
        
        let horasTotales = 0;
        if (horaInicio && horaFin) {
          const [horaIni, minIni] = horaInicio.split(':').map(Number);
          const [horaFinNum, minFin] = horaFin.split(':').map(Number);
          
          let minutosInicio = horaIni * 60 + minIni;
          let minutosFin = horaFinNum * 60 + minFin;
          
          // Si la hora de fin es menor que la de inicio, es turno nocturno
          if (minutosFin < minutosInicio) {
            minutosFin += 24 * 60; // Añadir 24 horas
          }
          
          horasTotales = (minutosFin - minutosInicio) / 60;
        }
        
        const diaAPI = {
          cuadd_dd: dia,
          cuadd_hhini: horaInicio,
          cuadd_hhfin: horaFin,
          cuadd_hhtotd: horasTotales,
          cuadd_tarea: "01" // Tarea por defecto
        };
        
        turnoAPI.Dias.push(diaAPI);
      }
      
      turnosAPI.push(turnoAPI);
    }
    
    return turnosAPI;
  } catch (error) {
    console.error('Error al convertir turnos para API:', error);
    throw error;
  }
};

/**
 * Actualiza un cuadrante completo desde Firestore a la API
 * @param {string} cuadranteId - ID del cuadrante en Firestore
 * @param {Object} currentService - Información del servicio actual
 * @param {Object} detallesServicio - Detalles del servicio
 * @returns {Promise} - Promesa con la respuesta de la actualización
 */
/**
 * Obtiene los cuadrantes desde Freematica
 * @param {string} año - Año para filtrar (ej: "2025")
 * @param {string} codPersona - Código de la persona para filtrar (opcional)
 * @param {Object} options - Opciones adicionales (orden, items, page, columns)
 * @returns {Promise} - Promesa con los datos de los cuadrantes
 */
export const obtenerCuadrantes = async (año, codPersona = null, options = {}) => {
  try {
    console.log('Obteniendo cuadrantes desde Freematica...');
    console.log('Parámetros:', { año, codPersona, options });
    
    const url = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes`;
    const params = {};
    
    // Construir el rquery base para el año
    let rquery = `CUADT_CAL==${año}`;
    
    // Si se especifica una persona, agregar el filtro
    if (codPersona) {
      rquery += `;CUADT_PERSONA==${codPersona}`;
    }
    
    params.rquery = rquery;
    
    // Parámetros opcionales
    if (options.orden) params.orden = options.orden;
    if (options.items) params.items = options.items;
    if (options.page) params.page = options.page;
    if (options.columns) params.columns = options.columns;
    
    console.log('URL:', url);
    console.log('Parámetros:', params);
    console.log('Headers:', getHeaders());
    
    const response = await axios.get(url, {
      params,
      headers: getHeaders()
    });

    console.log('Respuesta de la API:', response.data);
    
    if (response.data && response.data.errorCode === '200') {
      console.log('✅ Cuadrantes obtenidos correctamente');
      return response.data.data || [];
    } else {
      console.error('❌ Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener cuadrantes');
    }
  } catch (error) {
    console.error('❌ Error al obtener cuadrantes:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    throw error;
  }
};

/**
 * Obtiene los cuadrantes de una persona específica
 * @param {string} codPersona - Código de la persona
 * @param {string} año - Año para filtrar (ej: "2025")
 * @param {Object} options - Opciones adicionales
 * @returns {Promise} - Promesa con los datos de los cuadrantes de la persona
 */
export const obtenerCuadrantesPersona = async (codPersona, año, options = {}) => {
  return await obtenerCuadrantes(año, codPersona, options);
};

/**
 * Obtiene todos los cuadrantes de un año
 * @param {string} año - Año para filtrar (ej: "2025")
 * @param {Object} options - Opciones adicionales
 * @returns {Promise} - Promesa con los datos de todos los cuadrantes
 */
export const obtenerTodosCuadrantes = async (año, options = {}) => {
  return await obtenerCuadrantes(año, null, options);
};

/**
 * Obtiene los tramos de todos los vigilantes de un servicio
 * @param {Array} vigilantes - Array de vigilantes del servicio
 * @param {Object} detallesServicio - Detalles del servicio
 * @returns {Promise} - Promesa con los tramos de todos los vigilantes
 */
/**
 * Obtiene los tramos de una persona específica
 * @param {string} empresa - Código de la empresa
 * @param {string} delegacion - Código de la delegación
 * @param {string} codPersona - Código de la persona
 * @returns {Promise} - Promesa con los datos de los tramos de la persona
 */
export const obtenerTramosPersona = async (empresa, delegacion, codPersona) => {
  try {
    console.log(`Obteniendo tramos para persona ${codPersona} en empresa ${empresa}, delegación ${delegacion}`);
    
    const url = `${freematicaConfig.apiUrl}/pers/v1/personal/tramos`;
    const params = {
      empresa: empresa,
      delegacion: delegacion,
      persona: codPersona
    };
    
    console.log('URL:', url);
    console.log('Parámetros:', params);
    console.log('Headers:', getHeaders(empresa));
    
    const response = await axios.get(url, {
      params,
      headers: getHeaders(empresa)
    });

    console.log('Respuesta de la API:', response.data);
    
    if (response.data && response.data.errorCode === '200') {
      console.log('✅ Tramos de persona obtenidos correctamente');
      return response.data.data || [];
    } else {
      console.error('❌ Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener tramos de persona');
    }
  } catch (error) {
    console.error('❌ Error al obtener tramos de persona:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    throw error;
  }
};



/**
 * Obtiene los tramos modificados desde una fecha específica (V2)
 * @param {string} fchmodificacion - Fecha de última modificación (formato: YYYY-MM-DD)
 * @param {string} empresa - Código de la empresa (opcional)
 * @param {string} delegacion - Código de la delegación (opcional)
 * @param {Object} options - Opciones adicionales (orden, q, fq, items, page, columns)
 * @returns {Promise} - Promesa con los datos de los tramos modificados
 */
export const obtenerTramosModificados = async (fchmodificacion, empresa = null, delegacion = null, options = {}) => {
  try {
    console.log('Obteniendo tramos modificados desde Freematica V2...');
    console.log('Parámetros:', { fchmodificacion, empresa, delegacion, options });
    
    const url = `${freematicaConfig.apiUrl}/pers/v2/personal/tramos`;
    const params = {
      fchmodificacion: fchmodificacion
    };
    
    // Parámetros opcionales
    if (empresa) params.empresa = empresa;
    if (delegacion) params.delegacion = delegacion;
    if (options.orden) params.orden = options.orden;
    if (options.q) params.q = options.q;
    if (options.fq) params.fq = options.fq;
    if (options.items) params.items = options.items;
    if (options.page) params.page = options.page;
    if (options.columns) params.columns = options.columns;
    if (options.persona) params.persona = options.persona;
    
    console.log('URL:', url);
    console.log('Parámetros:', params);
    console.log('Headers:', getHeaders());
    
    const response = await axios.get(url, {
      params,
      headers: getHeaders()
    });

    console.log('Respuesta de la API V2:', response.data);
    
    if (response.data && response.data.errorCode === '200') {
      console.log('✅ Tramos modificados obtenidos correctamente');
      return response.data.data || [];
    } else {
      console.error('❌ Error en la respuesta V2:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener tramos modificados');
    }
  } catch (error) {
    console.error('❌ Error al obtener tramos modificados:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    throw error;
  }
};

/**
 * Obtiene los tramos modificados para un servicio específico
 * @param {string} fchmodificacion - Fecha de última modificación
 * @param {Object} detallesServicio - Detalles del servicio
 * @param {Array} vigilantes - Array de vigilantes del servicio
 * @param {string} personaFiltro - Código de persona específica para filtrar (opcional)
 * @returns {Promise} - Promesa con los tramos modificados organizados por vigilante
 */
export const obtenerTramosModificadosServicio = async (fchmodificacion, detallesServicio, vigilantes = [], personaFiltro = null) => {
  try {
    console.log('Obteniendo tramos modificados para el servicio...');
    console.log('Fecha modificación:', fchmodificacion);
    console.log('Vigilantes:', vigilantes.length);
    if (personaFiltro) {
      console.log('Filtro por persona:', personaFiltro);
    }
    
    // Extraer datos de localización del servicio
    const datosLocalizacion = extraerDatosLocalizacion(detallesServicio.localizacion);
    const empresa = datosLocalizacion.empresa || detallesServicio.empresa || "0001";
    const delegacion = detallesServicio.delegacion || "06";
    const servicio = datosLocalizacion.servicio;
    
    console.log('Datos extraídos:', { empresa, delegacion, servicio });
    
    // Si hay filtro por persona, obtener tramos solo para esa persona
    if (personaFiltro) {
      console.log(`Obteniendo tramos modificados solo para persona: ${personaFiltro}`);
      
      // Buscar el vigilante específico
      const vigilanteEspecifico = vigilantes.find(v => v.persona === personaFiltro);
      if (!vigilanteEspecifico) {
        throw new Error(`No se encontró el vigilante con código de persona: ${personaFiltro}`);
      }
      
      // Obtener tramos modificados con filtro de persona
      const tramosModificados = await obtenerTramosModificados(
        fchmodificacion,
        empresa,
        delegacion,
        { persona: personaFiltro }
      );
      
      console.log(`Se encontraron ${tramosModificados.length} tramos modificados para ${personaFiltro}`);
      
      // Organizar tramos para el vigilante específico
      const tramosPorVigilante = {};
      if (tramosModificados.length > 0) {
        tramosPorVigilante[personaFiltro] = {
          vigilante: vigilanteEspecifico,
          tramos: tramosModificados
        };
      }
      
      console.log(`Tramos organizados para 1 vigilante (filtrado)`);
      return tramosPorVigilante;
    }
    
    // Obtener todos los tramos modificados para la empresa/delegación
    const tramosModificados = await obtenerTramosModificados(
      fchmodificacion,
      empresa,
      delegacion
    );
    
    console.log(`Se encontraron ${tramosModificados.length} tramos modificados`);
    
    // Organizar tramos por vigilante
    const tramosPorVigilante = {};
    
    // Crear mapa de códigos de persona para vigilantes
    const vigilantesPorCodigo = {};
    vigilantes.forEach(vigilante => {
      if (vigilante.persona) {
        vigilantesPorCodigo[vigilante.persona] = vigilante;
      }
    });
    
    // Filtrar tramos que correspondan a vigilantes del servicio
    tramosModificados.forEach(tramo => {
      const codPersona = tramo.persona || tramo.codPersona;
      
      if (codPersona && vigilantesPorCodigo[codPersona]) {
        if (!tramosPorVigilante[codPersona]) {
          tramosPorVigilante[codPersona] = {
            vigilante: vigilantesPorCodigo[codPersona],
            tramos: []
          };
        }
        tramosPorVigilante[codPersona].tramos.push(tramo);
      }
    });
    
    console.log(`Tramos organizados para ${Object.keys(tramosPorVigilante).length} vigilantes`);
    return tramosPorVigilante;
  } catch (error) {
    console.error('Error al obtener tramos modificados del servicio:', error);
    throw error;
  }
};

export const obtenerTramosVigilantesServicio = async (vigilantes, detallesServicio) => {
  try {
    console.log('Obteniendo tramos de vigilantes del servicio...');
    console.log('Vigilantes:', vigilantes.length);
    
    const tramosPorVigilante = {};
    
    for (const vigilante of vigilantes) {
      try {
        const codPersona = vigilante.persona;
        if (!codPersona) {
          console.warn(`Vigilante sin código de persona, omitiendo:`, vigilante);
          continue;
        }
        
        console.log(`Obteniendo tramos para vigilante: ${vigilante.nombre} ${vigilante.apellidos} (${codPersona})`);
        
        // Extraer datos de localización del servicio
        const datosLocalizacion = extraerDatosLocalizacion(detallesServicio.localizacion);
        
        const tramos = await obtenerTramosPersona(
          datosLocalizacion.empresa || detallesServicio.empresa || "0001",
          detallesServicio.delegacion || "06",
          codPersona
        );
        
        if (tramos && tramos.length > 0) {
          tramosPorVigilante[codPersona] = {
            vigilante: vigilante,
            tramos: tramos
          };
          console.log(`✅ Se encontraron ${tramos.length} tramos para ${vigilante.nombre}`);
        } else {
          console.log(`⚠️ No se encontraron tramos para ${vigilante.nombre}`);
        }
      } catch (error) {
        console.error(`Error obteniendo tramos para vigilante ${vigilante.nombre}:`, error);
      }
    }
    
    console.log(`✅ Proceso completado. Tramos obtenidos para ${Object.keys(tramosPorVigilante).length} vigilantes`);
    return tramosPorVigilante;
  } catch (error) {
    console.error('Error al obtener tramos de vigilantes del servicio:', error);
    throw error;
  }
};

export const actualizarCuadranteCompleto = async (cuadranteId, currentService, detallesServicio) => {
  try {
    // Obtener todos los turnos del cuadrante desde Firestore
    const turnos = await obtenerTurnosPorCuadrante(cuadranteId, currentService);
    
    if (turnos.length === 0) {
      throw new Error('No hay turnos en el cuadrante para actualizar');
    }
    
    // Convertir turnos al formato de la API
    const turnosAPI = await convertirTurnosParaAPI(turnos, detallesServicio);
    
    if (turnosAPI.length === 0) {
      throw new Error('No se pudieron convertir los turnos al formato de la API');
    }
    
    // Construir el ID del cuadrante: Empresa__Delegación__Calendario__Mes__Contrato__Servicio
    const datosLocalizacion = extraerDatosLocalizacion(detallesServicio.localizacion);
    const empresa = datosLocalizacion.empresa || detallesServicio.empresa || "0001";
    const delegacion = datosLocalizacion.delegacion || detallesServicio.delegacion || "06";
    const servicio = datosLocalizacion.servicio;
    const contrato = datosLocalizacion.contrato;
    
    const primerTurno = turnos[0];
    const fecha = new Date(primerTurno.fecha);
    const calendario = fecha.getFullYear().toString();
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    
    const idReg = construirIdReg(empresa, delegacion, calendario, mes, contrato, servicio);
    
    console.log('Actualizando cuadrante con ID:', idReg);
    console.log('Datos a enviar:', JSON.stringify(turnosAPI, null, 2));
    
    // Actualizar el cuadrante en la API
    const resultado = await actualizarCuadrante(idReg, turnosAPI);
    
    return {
      idReg,
      turnosEnviados: turnosAPI.length,
      resultado
    };
  } catch (error) {
    console.error('Error al actualizar cuadrante completo:', error);
    throw error;
  }
};

/**
 * Obtiene los cuadrantes de Freematica para el año actual y personas específicas
 * @param {string} año - Año para filtrar (ej: "2025")
 * @param {Array} personas - Array de códigos de personas (ej: ["17005"])
 * @param {string} servicio - Código del servicio (ej: "03")
 * @returns {Promise} - Promesa con los datos de los cuadrantes
 */
export const obtenerCuadrantesFreematica = async (año, personas = [], servicio = "03") => {
  try {
    console.log('Obteniendo cuadrantes desde Freematica...');
    console.log('Parámetros:', { año, personas, servicio });
    
    const url = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes`;
    const params = {};
    
    // Construir el rquery base para el año
    let rquery = `CUADT_CAL==${año}`;
    
    // Si se especifican personas, agregar el filtro
    if (personas && Array.isArray(personas) && personas.length > 0) {
      const personasQuery = personas.map(p => `CUADT_PERSONA==${p}`).join(';');
      rquery += `;${personasQuery}`;
    }
    
    // Agregar filtro de servicio
    rquery += `;CUADT_SERV==${servicio}`;
    
    params.rquery = rquery;
    
    console.log('URL:', url);
    console.log('Query:', rquery);
    console.log('Headers:', getHeaders());
    
    const response = await axios.get(url, {
      params,
      headers: getHeaders()
    });

    console.log('Respuesta de la API:', response.data);
    console.log('Tipo de respuesta.data:', typeof response.data);
    console.log('response.data.data:', response.data.data);
    console.log('Es array response.data.data?', Array.isArray(response.data.data));
    
    if (response.data && response.data.errorCode === '200') {
      console.log('✅ Cuadrantes obtenidos correctamente');
      
      // Extraer los datos de la estructura anidada
      let cuadrantes = [];
      
      if (response.data.data && response.data.data.items) {
        // Los datos están en response.data.data.items
        cuadrantes = response.data.data.items;
        console.log('📊 Datos extraídos de response.data.data.items:', cuadrantes.length, 'elementos');
      } else if (response.data.data && Array.isArray(response.data.data)) {
        // Los datos están directamente en response.data.data
        cuadrantes = response.data.data;
        console.log('📊 Datos extraídos de response.data.data:', cuadrantes.length, 'elementos');
      } else if (response.data.data && typeof response.data.data === 'object') {
        // Convertir objeto único a array
        cuadrantes = [response.data.data];
        console.log('📊 Objeto único convertido a array:', cuadrantes.length, 'elementos');
      } else {
        console.warn('⚠️ No se encontraron datos en la respuesta');
        cuadrantes = [];
      }
      
      // Asegurar que es un array
      if (!Array.isArray(cuadrantes)) {
        console.warn('⚠️ Los datos no son un array, convirtiendo...');
        cuadrantes = [cuadrantes];
      }
      
      console.log('📊 Cuadrantes finales:', cuadrantes);
      return cuadrantes;
    } else {
      console.error('❌ Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener cuadrantes');
    }
  } catch (error) {
    console.error('❌ Error al obtener cuadrantes:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    throw error;
  }
};

/**
 * Obtiene los turnos de vigilantes desde Freematica
 * @param {Array} vigilantes - Array de vigilantes del servicio
 * @param {string} año - Año para filtrar (ej: "2025")
 * @param {string} servicio - Código del servicio (ej: "03")
 * @returns {Promise} - Promesa con los turnos de los vigilantes
 */
export const obtenerTurnosVigilantesFreematica = async (vigilantes, año, servicio = "03") => {
  try {
    console.log('Obteniendo turnos de vigilantes desde Freematica...');
    console.log('Vigilantes:', vigilantes.length);
    console.log('Año:', año);
    console.log('Servicio:', servicio);
    
    // Extraer códigos de persona de los vigilantes
    const personas = vigilantes
      .filter(vigilante => vigilante.persona && vigilante.persona.trim() !== '')
      .map(vigilante => vigilante.persona);
    
    if (personas.length === 0) {
      console.warn('⚠️ No hay vigilantes con códigos de persona válidos');
      return [];
    }
    
    console.log('Códigos de persona a consultar:', personas);
    
    // Obtener cuadrantes de Freematica
    const cuadrantes = await obtenerCuadrantesFreematica(año, personas, servicio);
    
    if (cuadrantes.length === 0) {
      console.log('⚠️ No se encontraron cuadrantes para los vigilantes');
      return [];
    }
    
    console.log(`Se encontraron ${cuadrantes.length} cuadrantes`);
    
    // Procesar y formatear los datos para mostrar
    const turnosFormateados = cuadrantes.map(cuadrante => {
      return {
        vigilante: {
          codigo: cuadrante.CUADT_PERSONA,
          nombre: `${cuadrante.VSSPER_NOM} ${cuadrante.VSSPER_APELL1} ${cuadrante.VSSPER_APELL2 || ''}`.trim(),
          nif: cuadrante.VSSPER_NIF,
          nifDC: cuadrante.VSSPER_NIF_DC
        },
        servicio: {
          empresa: cuadrante.CUADT_EMP,
          delegacion: cuadrante.CUADT_DELEG,
          contrato: cuadrante.CUADT_CTRT,
          servicio: cuadrante.CUADT_SERV,
          calendario: cuadrante.CUADT_CAL,
          mes: cuadrante.CUADT_MES,
          turnoOper: cuadrante.CUADT_TURNOPER
        },
        horas: {
          totales: cuadrante.CUADT_HHTOT,
          totalesB: cuadrante.CUADT_HHTOTB,
          nocturnas: cuadrante.CUADT_HHNOC,
          festivas: cuadrante.CUADT_HHFEST,
          nocturnasFestivas: cuadrante.CUADT_HHNOCP,
          nochesFestivas: cuadrante.CUADT_NOCFEST
        },
        cuadrante: cuadrante
      };
    });
    
    console.log(`✅ Turnos formateados para ${turnosFormateados.length} vigilantes`);
    return turnosFormateados;
  } catch (error) {
    console.error('❌ Error al obtener turnos de vigilantes:', error);
    throw error;
  }
};

/**
 * Obtiene los turnos desde Freematica usando las tablas VSS_CUADRANTE_T y VSS_CUADRANTE_D
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar (opcional)
 * @param {string} dia - Día para filtrar (opcional)
 * @param {string} codPersona - Código de persona para filtrar (opcional)
 * @returns {Promise} - Promesa con los datos de los turnos
 */
export const obtenerTurnosCuadrantes = async (empresa, delegacion, contrato, servicio, año, mes = null, dia = null, codPersona = null) => {
  try {
    console.log('🔍 OBTENIENDO TURNOS DESDE CUADRANTES...');
    console.log('📋 PARÁMETROS RECIBIDOS:', { 
      empresa, 
      delegacion, 
      contrato, 
      servicio, 
      año, 
      mes, 
      dia, 
      codPersona 
    });
    console.log('🔍 TIPOS DE DATOS:', {
      empresa: typeof empresa,
      delegacion: typeof delegacion,
      contrato: typeof contrato,
      servicio: typeof servicio,
      año: typeof año
    });
    
    // Primero obtener los datos de cuadrantes (VSS_CUADRANTE_T) para obtener las personas
    const urlCuadrantes = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes`;
    const paramsCuadrantes = {};
    
    // Construir el rquery para cuadrantes
    let rqueryCuadrantes = `CUADT_EMP==${empresa};CUADT_DELEG==${delegacion};CUADT_CTRT==${contrato};CUADT_SERV==${servicio};CUADT_CAL==${año}`;
    
    if (mes) {
      rqueryCuadrantes += `;CUADT_MES==${mes}`;
    }
    
    if (codPersona) {
      rqueryCuadrantes += `;CUADT_PERSONA==${codPersona}`;
    }
    
    paramsCuadrantes.rquery = rqueryCuadrantes;
    
    console.log('🔍 CONSULTA CUADRANTES:');
    console.log('URL:', urlCuadrantes);
    console.log('Query:', rqueryCuadrantes);
    
    // Agregar delay antes de la primera petición
    await delay(1000);
    
    const responseCuadrantes = await axios.get(urlCuadrantes, {
      params: paramsCuadrantes,
      headers: getHeaders()
    });
    
    console.log('📊 Respuesta de cuadrantes:', responseCuadrantes.data);
    
    if (!responseCuadrantes.data || responseCuadrantes.data.errorCode !== '200') {
      throw new Error('Error al obtener datos de cuadrantes: ' + (responseCuadrantes.data?.errorMessage || 'Error desconocido'));
    }
    
    // Extraer datos de cuadrantes
    let cuadrantes = [];
    if (responseCuadrantes.data.data && responseCuadrantes.data.data.items) {
      cuadrantes = responseCuadrantes.data.data.items;
    } else if (responseCuadrantes.data.data && Array.isArray(responseCuadrantes.data.data)) {
      cuadrantes = responseCuadrantes.data.data;
    } else if (responseCuadrantes.data.data && typeof responseCuadrantes.data.data === 'object') {
      cuadrantes = [responseCuadrantes.data.data];
    }
    
    if (!Array.isArray(cuadrantes)) {
      cuadrantes = [cuadrantes];
    }
    
    console.log(`📊 Cuadrantes obtenidos: ${cuadrantes.length} elementos`);
    
    // Debug: Mostrar información de los cuadrantes
    if (cuadrantes.length > 0) {
      console.log('📋 Campos disponibles en cuadrantes:', Object.keys(cuadrantes[0]));
      console.log('👥 Personas en cuadrantes:', cuadrantes.map(c => c.CUADT_PERSONA).filter((v, i, a) => a.indexOf(v) === i));
    }
    
    // Ahora obtener los datos de cuadrantes-detalles (VSS_CUADRANTE_D)
    const urlDetalles = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes-detalles`;
    const paramsDetalles = {};
    
    // Construir el rquery base - solo campos de VSS_CUADRANTE_D
    let rqueryDetalles = `CUADD_EMP==${empresa};CUADD_DELEG==${delegacion};CUADD_CTRT==${contrato};CUADD_SERV==${servicio};CUADD_CAL==${año}`;
    
    // Agregar filtros opcionales
    if (mes) {
      rqueryDetalles += `;CUADD_MES==${mes}`;
    }
    
    if (dia) {
      rqueryDetalles += `;CUADD_DD==${dia}`;
    }
    
    paramsDetalles.rquery = rqueryDetalles;
    
    console.log('🔍 CONSULTA DETALLES:');
    console.log('URL:', urlDetalles);
    console.log('Query:', rqueryDetalles);
    
    // Agregar delay antes de la segunda petición
    await delay(1000);
    
    const responseDetalles = await axios.get(urlDetalles, {
      params: paramsDetalles,
      headers: getHeaders()
    });

    console.log('📊 Respuesta de cuadrantes-detalles:', responseDetalles.data);
    
    if (!responseDetalles.data || responseDetalles.data.errorCode !== '200') {
      throw new Error('Error al obtener datos de cuadrantes-detalles: ' + (responseDetalles.data?.errorMessage || 'Error desconocido'));
    }
    
    // Extraer datos de cuadrantes-detalles
    let detalles = [];
    if (responseDetalles.data.data && responseDetalles.data.data.items) {
      detalles = responseDetalles.data.data.items;
    } else if (responseDetalles.data.data && Array.isArray(responseDetalles.data.data)) {
      detalles = responseDetalles.data.data;
    } else if (responseDetalles.data.data && typeof responseDetalles.data.data === 'object') {
      detalles = [responseDetalles.data.data];
    }
    
    if (!Array.isArray(detalles)) {
      detalles = [detalles];
    }
    
    console.log(`📊 Detalles obtenidos: ${detalles.length} elementos`);
    
    // Debug: Mostrar información de los detalles
    if (detalles.length > 0) {
      console.log('📋 Campos disponibles en detalles:', Object.keys(detalles[0]));
      console.log('📅 Días en detalles:', detalles.map(d => d.CUADD_DD).filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b));
    }
    
    // Crear un mapa de cuadrantes por clave única
    const cuadrantesMap = new Map();
    cuadrantes.forEach(cuadrante => {
      const key = `${cuadrante.CUADT_EMP}_${cuadrante.CUADT_DELEG}_${cuadrante.CUADT_CTRT}_${cuadrante.CUADT_SERV}_${cuadrante.CUADT_CAL}_${cuadrante.CUADT_MES}_${cuadrante.CUADT_TURNOPER}`;
      cuadrantesMap.set(key, cuadrante);
    });
    
    console.log(`🗺️ Mapa de cuadrantes creado con ${cuadrantesMap.size} elementos`);
    
    // Debug: Mostrar algunas claves de cuadrantes
    if (cuadrantes.length > 0) {
      console.log('🔍 Ejemplo de clave de cuadrante:', `${cuadrantes[0].CUADT_EMP}_${cuadrantes[0].CUADT_DELEG}_${cuadrantes[0].CUADT_CTRT}_${cuadrantes[0].CUADT_SERV}_${cuadrantes[0].CUADT_CAL}_${cuadrantes[0].CUADT_MES}_${cuadrantes[0].CUADT_TURNOPER}`);
      console.log('📋 Primer cuadrante:', cuadrantes[0]);
    }
    
    // Combinar datos de detalles con datos de cuadrantes
    const turnosCombinados = [];
    let coincidenciasEncontradas = 0;
    let totalDetalles = detalles.length;
    
    detalles.forEach((detalle, index) => {
      // Buscar el cuadrante correspondiente
      const key = `${detalle.CUADD_EMP}_${detalle.CUADD_DELEG}_${detalle.CUADD_CTRT}_${detalle.CUADD_SERV}_${detalle.CUADD_CAL}_${detalle.CUADD_MES}_${detalle.CUADD_TURNOPER}`;
      const cuadrante = cuadrantesMap.get(key);
      
      if (cuadrante) {
        coincidenciasEncontradas++;
        // Combinar datos
        const turnoCombinado = {
          ...detalle,
          CUADT_PERSONA: cuadrante.CUADT_PERSONA,
          VSSPER_NOM: cuadrante.VSSPER_NOM,
          VSSPER_APELL1: cuadrante.VSSPER_APELL1,
          VSSPER_APELL2: cuadrante.VSSPER_APELL2,
          VSSPER_NIF: cuadrante.VSSPER_NIF,
          CUADT_TURNOPER: cuadrante.CUADT_TURNOPER
        };
        
        turnosCombinados.push(turnoCombinado);
      } else {
        // Debug: Mostrar detalles que no coinciden
        if (index < 3) { // Solo mostrar los primeros 3 para no saturar el log
          console.log(`❌ Detalle sin coincidencia (${index + 1}/${totalDetalles}):`, {
            key,
            detalle: {
              EMP: detalle.CUADD_EMP,
              DELEG: detalle.CUADD_DELEG,
              CTRT: detalle.CUADD_CTRT,
              SERV: detalle.CUADD_SERV,
              CAL: detalle.CUADD_CAL,
              MES: detalle.CUADD_MES,
              TURNOPER: detalle.CUADD_TURNOPER
            }
          });
        }
      }
    });
    
    console.log(`🔗 Coincidencias encontradas: ${coincidenciasEncontradas}/${totalDetalles} detalles`);
    
    // Si no hay coincidencias, intentar una estrategia alternativa
    if (coincidenciasEncontradas === 0 && detalles.length > 0) {
      console.log('⚠️ No se encontraron coincidencias exactas. Intentando estrategia alternativa...');
      
      // Estrategia alternativa: usar solo los datos de detalles y buscar la persona en cuadrantes
      detalles.forEach(detalle => {
        // Buscar cualquier cuadrante que coincida con los campos principales
        const cuadranteEncontrado = cuadrantes.find(cuadrante => 
          cuadrante.CUADT_EMP === detalle.CUADD_EMP &&
          cuadrante.CUADT_DELEG === detalle.CUADD_DELEG &&
          cuadrante.CUADT_CTRT === detalle.CUADD_CTRT &&
          cuadrante.CUADT_SERV === detalle.CUADD_SERV &&
          cuadrante.CUADT_CAL === detalle.CUADD_CAL &&
          cuadrante.CUADT_MES === detalle.CUADD_MES
        );
        
        if (cuadranteEncontrado) {
          const turnoCombinado = {
            ...detalle,
            CUADT_PERSONA: cuadranteEncontrado.CUADT_PERSONA,
            VSSPER_NOM: cuadranteEncontrado.VSSPER_NOM,
            VSSPER_APELL1: cuadranteEncontrado.VSSPER_APELL1,
            VSSPER_APELL2: cuadranteEncontrado.VSSPER_APELL2,
            VSSPER_NIF: cuadranteEncontrado.VSSPER_NIF,
            CUADT_TURNOPER: cuadranteEncontrado.CUADT_TURNOPER
          };
          
          turnosCombinados.push(turnoCombinado);
        }
      });
      
      console.log(`🔄 Estrategia alternativa: ${turnosCombinados.length} turnos encontrados`);
    }
    
    console.log(`🔗 Turnos combinados: ${turnosCombinados.length} elementos`);
    
    // Filtrar por persona si se especifica
    if (codPersona) {
      const turnosFiltrados = turnosCombinados.filter(turno => turno.CUADT_PERSONA == codPersona);
      console.log(`🔍 Filtrado por persona ${codPersona}: ${turnosFiltrados.length} turnos encontrados`);
      turnosCombinados.length = 0;
      turnosCombinados.push(...turnosFiltrados);
    }
    
    console.log('✅ Turnos obtenidos correctamente');
    
    // Formatear los datos para mejor uso con horas de inicio y fin
    const turnosFormateados = turnosCombinados.map(turno => {
      // Calcular horas de inicio y fin basadas en las horas totales
      let horaInicio = null;
      let horaFin = null;
      
      if (turno.CUADD_HHTOTD && turno.CUADD_HHTOTD > 0) {
        // Por defecto, asumimos turno de 8 horas (08:00 - 16:00)
        // Esto se puede personalizar según las necesidades
        const horasTotales = parseFloat(turno.CUADD_HHTOTD);
        
        if (horasTotales <= 8) {
          // Turno diurno estándar
          horaInicio = "08:00";
          const horaFinNum = 8 + horasTotales;
          horaFin = `${horaFinNum.toString().padStart(2, '0')}:00`;
        } else if (horasTotales <= 12) {
          // Turno extendido
          horaInicio = "06:00";
          const horaFinNum = 6 + horasTotales;
          horaFin = `${horaFinNum.toString().padStart(2, '0')}:00`;
        } else {
          // Turno nocturno o especial
          horaInicio = "22:00";
          const horaFinNum = (22 + horasTotales) % 24;
          horaFin = `${horaFinNum.toString().padStart(2, '0')}:00`;
        }
      }
      
      return {
        empresa: turno.CUADD_EMP,
        delegacion: turno.CUADD_DELEG,
        contrato: turno.CUADD_CTRT,
        servicio: turno.CUADD_SERV,
        año: turno.CUADD_CAL,
        mes: turno.CUADD_MES,
        dia: turno.CUADD_DD,
        horasTotales: turno.CUADD_HHTOTD,
        horaInicio: horaInicio,
        horaFin: horaFin,
        codigoPersona: turno.CUADT_PERSONA || 'N/A',
        // Datos adicionales de la persona si están disponibles
        nombrePersona: turno.VSSPER_NOM || '',
        apellido1: turno.VSSPER_APELL1 || '',
        apellido2: turno.VSSPER_APELL2 || '',
        nif: turno.VSSPER_NIF || '',
        // Datos del turno
        turnoOper: turno.CUADT_TURNOPER || 'N/A',
        // Datos originales
        datosOriginales: turno
      };
    });
    
    console.log(`📊 Turnos formateados: ${turnosFormateados.length} elementos`);
    return turnosFormateados;
  } catch (error) {
    console.error('❌ Error al obtener turnos:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    throw error;
  }
};


/**
 * Guarda un turno en la base de datos local
 * @param {Object} turno - Datos del turno a guardar
 * @param {string} servicioId - ID del servicio
 * @returns {Promise} - Promesa con el resultado del guardado
 */
export const guardarTurnoEnBD = async (turno, servicioId) => {
  try {
    console.log('Guardando turno en BD:', turno);
    
    // Importar Firebase
    const { db } = await import('../../../firebase');
    
    // Crear el documento del turno
    const turnoData = {
      servicioId: servicioId,
      vigilante: turno.codigoPersona,
      fecha: `${turno.año}-${turno.mes.padStart(2, '0')}-${turno.dia.padStart(2, '0')}`,
      inicioTurno: turno.horaInicio,
      finTurno: turno.horaFin,
      horasTotales: turno.horasTotales,
      empresa: turno.empresa,
      delegacion: turno.delegacion,
      contrato: turno.contrato,
      servicio: turno.servicio,
      turnoOper: turno.turnoOper,
      // Datos adicionales
      nombrePersona: turno.nombrePersona,
      apellido1: turno.apellido1,
      apellido2: turno.apellido2,
      nif: turno.nif,
      // Metadatos
      origen: 'freematica',
      fechaCreacion: new Date(),
      fechaActualizacion: new Date(),
      // Datos originales de Freematica
      datosFreematica: turno.datosOriginales
    };
    
    // Guardar en Firestore
    const docRef = await db.collection('turnos').add(turnoData);
    
    console.log('✅ Turno guardado con ID:', docRef.id);
    
    return {
      id: docRef.id,
      turno: turnoData,
      success: true
    };
  } catch (error) {
    console.error('❌ Error al guardar turno en BD:', error);
    throw error;
  }
};

/**
 * Guarda múltiples turnos en la base de datos
 * @param {Array} turnos - Array de turnos a guardar
 * @param {string} servicioId - ID del servicio
 * @returns {Promise} - Promesa con el resultado del guardado
 */
export const guardarTurnosEnBD = async (turnos, servicioId) => {
  try {
    console.log(`Guardando ${turnos.length} turnos en BD...`);
    
    const resultados = {
      guardados: [],
      errores: [],
      total: turnos.length,
      exitosos: 0,
      fallidos: 0
    };
    
    for (const turno of turnos) {
      try {
        const resultado = await guardarTurnoEnBD(turno, servicioId);
        resultados.guardados.push(resultado);
        resultados.exitosos++;
      } catch (error) {
        console.error(`Error guardando turno:`, error);
        resultados.errores.push({
          turno: turno,
          error: error.message
        });
        resultados.fallidos++;
      }
    }
    
    console.log(`✅ Proceso completado: ${resultados.exitosos} guardados, ${resultados.fallidos} fallidos`);
    return resultados;
  } catch (error) {
    console.error('❌ Error al guardar turnos en BD:', error);
    throw error;
  }
};

/**
 * Obtiene turnos desde Freematica y los guarda en la base de datos
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @param {string} servicioId - ID del servicio en la BD local
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar (opcional)
 * @param {string} dia - Día para filtrar (opcional)
 * @param {string} codPersona - Código de persona para filtrar (opcional)
 * @returns {Promise} - Promesa con el resultado del proceso
 */
export const obtenerYGuardarTurnos = async (empresa, delegacion, contrato, servicio, servicioId, año, mes = null, dia = null, codPersona = null) => {
  try {
    console.log('Obteniendo y guardando turnos...');
    
    // 1. Obtener turnos desde Freematica
    const turnos = await obtenerTurnosCuadrantes(
      empresa, delegacion, contrato, servicio, año, mes, dia, codPersona
    );
    
    if (turnos.length === 0) {
      console.log('⚠️ No se encontraron turnos para guardar');
      return {
        turnosObtenidos: 0,
        turnosGuardados: 0,
        errores: [],
        mensaje: 'No se encontraron turnos para guardar'
      };
    }
    
    console.log(`📊 Se obtuvieron ${turnos.length} turnos desde Freematica`);
    
    // 2. Guardar turnos en la base de datos
    const resultadoGuardado = await guardarTurnosEnBD(turnos, servicioId);
    
    return {
      turnosObtenidos: turnos.length,
      turnosGuardados: resultadoGuardado.exitosos,
      errores: resultadoGuardado.errores,
      turnosGuardadosDetalle: resultadoGuardado.guardados,
      mensaje: `Se guardaron ${resultadoGuardado.exitosos} de ${turnos.length} turnos`
    };
  } catch (error) {
    console.error('❌ Error en el proceso completo:', error);
    throw error;
  }
};

/**
 * Obtiene turnos de una persona específica y los guarda en la base de datos
 * @param {string} codPersona - Código de la persona
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @param {string} servicioId - ID del servicio en la BD local
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar (opcional)
 * @returns {Promise} - Promesa con el resultado del proceso
 */
export const obtenerYGuardarTurnosPersona = async (codPersona, empresa, delegacion, contrato, servicio, servicioId, año, mes = null) => {
  return await obtenerYGuardarTurnos(empresa, delegacion, contrato, servicio, servicioId, año, mes, null, codPersona);
};

/**
 * Obtiene todos los turnos de un servicio y los guarda en la base de datos
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @param {string} servicioId - ID del servicio en la BD local
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar (opcional)
 * @returns {Promise} - Promesa con el resultado del proceso
 */
export const obtenerYGuardarTurnosServicio = async (empresa, delegacion, contrato, servicio, servicioId, año, mes = null) => {
  return await obtenerYGuardarTurnos(empresa, delegacion, contrato, servicio, servicioId, año, mes);
};

/**
 * Calcula las horas de inicio y fin basadas en las horas totales y tipo de turno
 * @param {number} horasTotales - Horas totales del turno
 * @param {string} tipoTurno - Tipo de turno (diurno, nocturno, etc.)
 * @returns {Object} - Objeto con horaInicio y horaFin
 */
export const calcularHorasTurno = (horasTotales, tipoTurno = 'diurno') => {
  const horas = parseFloat(horasTotales);
  
  switch (tipoTurno.toLowerCase()) {
    case 'diurno':
      return {
        horaInicio: "08:00",
        horaFin: `${(8 + horas).toString().padStart(2, '0')}:00`
      };
    
    case 'tarde':
      return {
        horaInicio: "14:00",
        horaFin: `${(14 + horas).toString().padStart(2, '0')}:00`
      };
    
    case 'nocturno':
      return {
        horaInicio: "22:00",
        horaFin: `${((22 + horas) % 24).toString().padStart(2, '0')}:00`
      };
    
    case 'madrugada':
      return {
        horaInicio: "00:00",
        horaFin: `${horas.toString().padStart(2, '0')}:00`
      };
    
    case 'extendido':
      return {
        horaInicio: "06:00",
        horaFin: `${(6 + horas).toString().padStart(2, '0')}:00`
      };
    
    default:
      // Turno estándar de 8 horas
      return {
        horaInicio: "08:00",
        horaFin: `${(8 + horas).toString().padStart(2, '0')}:00`
      };
  }
};

/**
 * Actualiza las horas de inicio y fin de un turno existente
 * @param {string} turnoId - ID del turno en la BD
 * @param {string} horaInicio - Nueva hora de inicio
 * @param {string} horaFin - Nueva hora de fin
 * @returns {Promise} - Promesa con el resultado de la actualización
 */
export const actualizarHorasTurno = async (turnoId, horaInicio, horaFin) => {
  try {
    console.log(`Actualizando horas del turno ${turnoId}: ${horaInicio} - ${horaFin}`);
    
    // Importar Firebase
    const { db } = await import('../../../firebase');
    
    // Calcular horas totales
    const [horaIni, minIni] = horaInicio.split(':').map(Number);
    const [horaFinNum, minFin] = horaFin.split(':').map(Number);
    
    let minutosInicio = horaIni * 60 + minIni;
    let minutosFin = horaFinNum * 60 + minFin;
    
    // Si la hora de fin es menor que la de inicio, es turno nocturno
    if (minutosFin < minutosInicio) {
      minutosFin += 24 * 60; // Añadir 24 horas
    }
    
    const horasTotales = (minutosFin - minutosInicio) / 60;
    
    // Actualizar en Firestore
    await db.collection('turnos').doc(turnoId).update({
      inicioTurno: horaInicio,
      finTurno: horaFin,
      horasTotales: horasTotales,
      fechaActualizacion: new Date()
    });
    
    console.log('✅ Horas del turno actualizadas correctamente');
    
    return {
      id: turnoId,
      horaInicio: horaInicio,
      horaFin: horaFin,
      horasTotales: horasTotales,
      success: true
    };
  } catch (error) {
    console.error('❌ Error al actualizar horas del turno:', error);
    throw error;
  }
};

/**
 * Obtiene los turnos de una persona específica desde cuadrantes
 * @param {string} codPersona - Código de la persona
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar (opcional)
 * @returns {Promise} - Promesa con los turnos de la persona
 */
export const obtenerTurnosPersona = async (codPersona, empresa, delegacion, contrato, servicio, año, mes = null) => {
  return await obtenerTurnosCuadrantes(empresa, delegacion, contrato, servicio, año, mes, null, codPersona);
};

/**
 * Obtiene todos los turnos de un servicio para un período específico
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar (opcional)
 * @returns {Promise} - Promesa con todos los turnos del servicio
 */
export const obtenerTurnosServicio = async (empresa, delegacion, contrato, servicio, año, mes = null) => {
  return await obtenerTurnosCuadrantes(empresa, delegacion, contrato, servicio, año, mes);
};

/**
 * Obtiene los turnos de múltiples vigilantes de un servicio
 * @param {Array} vigilantes - Array de vigilantes del servicio
 * @param {Object} detallesServicio - Detalles del servicio
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar (opcional)
 * @returns {Promise} - Promesa con los turnos de todos los vigilantes
 */
export const obtenerTurnosVigilantesServicio = async (vigilantes, detallesServicio, año, mes = null) => {
  try {
    console.log('Obteniendo turnos de vigilantes del servicio...');
    console.log('Vigilantes:', vigilantes.length);
    console.log('Año:', año);
    console.log('Mes:', mes);
    
    // Extraer datos de localización del servicio
    const datosLocalizacion = extraerDatosLocalizacion(detallesServicio.localizacion);
    const empresa = datosLocalizacion.empresa || detallesServicio.empresa || "0001";
    const delegacion = detallesServicio.delegacion || "06";
    const contrato = datosLocalizacion.contrato;
    const servicio = datosLocalizacion.servicio;
    
    console.log('Datos extraídos:', { empresa, delegacion, contrato, servicio });
    
    const turnosPorVigilante = {};
    
    for (const vigilante of vigilantes) {
      try {
        const codPersona = vigilante.persona;
        if (!codPersona) {
          console.warn(`Vigilante sin código de persona, omitiendo:`, vigilante);
          continue;
        }
        
        console.log(`Obteniendo turnos para vigilante: ${vigilante.nombre} ${vigilante.apellidos} (${codPersona})`);
        
        const turnos = await obtenerTurnosPersona(
          codPersona,
          empresa,
          delegacion,
          contrato,
          servicio,
          año,
          mes
        );
        
        if (turnos && turnos.length > 0) {
          turnosPorVigilante[codPersona] = {
            vigilante: vigilante,
            turnos: turnos
          };
          console.log(`✅ Se encontraron ${turnos.length} turnos para ${vigilante.nombre}`);
        } else {
          console.log(`⚠️ No se encontraron turnos para ${vigilante.nombre}`);
        }
      } catch (error) {
        console.error(`Error obteniendo turnos para vigilante ${vigilante.nombre}:`, error);
      }
    }
    
    console.log(`✅ Proceso completado. Turnos obtenidos para ${Object.keys(turnosPorVigilante).length} vigilantes`);
    return turnosPorVigilante;
  } catch (error) {
    console.error('Error al obtener turnos de vigilantes del servicio:', error);
    throw error;
  }
};

/**
 * Función de prueba para diagnosticar el problema de turnos individuales
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código de contrato
 * @param {string} servicio - Código de servicio
 * @param {string} año - Año
 * @param {string} mes - Mes
 * @param {string} codPersona - Código de persona
 * @returns {Promise<Object>} - Información de diagnóstico
 */
export const diagnosticarTurnosIndividuales = async (empresa, delegacion, contrato, servicio, año, mes, codPersona) => {
  try {
    console.log('🔍 DIAGNÓSTICO: Iniciando análisis de turnos individuales...');
    console.log('📋 Parámetros:', { empresa, delegacion, contrato, servicio, año, mes, codPersona });
    
    // 1. Obtener datos de cuadrantes
    const urlCuadrantes = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes`;
    const rqueryCuadrantes = `CUADT_EMP==${empresa};CUADT_DELEG==${delegacion};CUADT_CTRT==${contrato};CUADT_SERV==${servicio};CUADT_CAL==${año};CUADT_MES==${mes};CUADT_PERSONA==${codPersona}`;
    
    console.log('🔍 Consultando cuadrantes con query:', rqueryCuadrantes);
    
    const responseCuadrantes = await axios.get(urlCuadrantes, {
      params: { rquery: rqueryCuadrantes },
      headers: getHeaders()
    });
    
    console.log('📊 Respuesta de cuadrantes:', responseCuadrantes.data);
    
    // 2. Obtener datos de cuadrantes-detalles
    const urlDetalles = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes-detalles`;
    const rqueryDetalles = `CUADD_EMP==${empresa};CUADD_DELEG==${delegacion};CUADD_CTRT==${contrato};CUADD_SERV==${servicio};CUADD_CAL==${año};CUADD_MES==${mes}`;
    
    console.log('🔍 Consultando detalles con query:', rqueryDetalles);
    
    const responseDetalles = await axios.get(urlDetalles, {
      params: { rquery: rqueryDetalles },
      headers: getHeaders()
    });
    
    console.log('📊 Respuesta de detalles:', responseDetalles.data);
    
    // 3. Analizar resultados
    const diagnostico = {
      cuadrantes: {
        url: urlCuadrantes,
        query: rqueryCuadrantes,
        response: responseCuadrantes.data,
        items: []
      },
      detalles: {
        url: urlDetalles,
        query: rqueryDetalles,
        response: responseDetalles.data,
        items: []
      },
      conclusion: ''
    };
    
    // Extraer items de cuadrantes
    if (responseCuadrantes.data && responseCuadrantes.data.errorCode === '200') {
      if (responseCuadrantes.data.data && responseCuadrantes.data.data.items) {
        diagnostico.cuadrantes.items = responseCuadrantes.data.data.items;
      } else if (responseCuadrantes.data.data && Array.isArray(responseCuadrantes.data.data)) {
        diagnostico.cuadrantes.items = responseCuadrantes.data.data;
      }
    }
    
    // Extraer items de detalles
    if (responseDetalles.data && responseDetalles.data.errorCode === '200') {
      if (responseDetalles.data.data && responseDetalles.data.data.items) {
        diagnostico.detalles.items = responseDetalles.data.data.items;
      } else if (responseDetalles.data.data && Array.isArray(responseDetalles.data.data)) {
        diagnostico.detalles.items = responseDetalles.data.data;
      }
    }
    
    // Análisis
    console.log(`📊 Cuadrantes encontrados: ${diagnostico.cuadrantes.items.length}`);
    console.log(`📊 Detalles encontrados: ${diagnostico.detalles.items.length}`);
    
    if (diagnostico.cuadrantes.items.length === 0) {
      diagnostico.conclusion = '❌ No se encontraron cuadrantes para esta persona';
    } else if (diagnostico.detalles.items.length === 0) {
      diagnostico.conclusion = '❌ No se encontraron detalles de turnos para este mes';
    } else {
      diagnostico.conclusion = '✅ Se encontraron datos, pero puede haber problema en la combinación';
    }
    
    console.log('🔍 DIAGNÓSTICO COMPLETO:', diagnostico);
    return diagnostico;
    
  } catch (error) {
    console.error('❌ Error en diagnóstico:', error);
    throw error;
  }
};

export const buscarDatosHistoricos = async (empresa, delegacion, contrato, servicio, codPersona) => {
  try {
    console.log('🔍 BUSCANDO DATOS HISTÓRICOS...');
    console.log('Parámetros:', { empresa, delegacion, contrato, servicio, codPersona });
    
    const resultados = {
      cuadrantesEncontrados: [],
      detallesEncontrados: [],
      resumen: {}
    };
    
    // Buscar en los últimos 3 años
    const añosActuales = [2023, 2024, 2025];
    
    for (const año of añosActuales) {
      for (let mes = 1; mes <= 12; mes++) {
        try {
          // Consultar cuadrantes
          const urlCuadrantes = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes`;
          const rqueryCuadrantes = `CUADT_EMP==${empresa};CUADT_DELEG==${delegacion};CUADT_CTRT==${contrato};CUADT_SERV==${servicio};CUADT_CAL==${año};CUADT_MES==${mes};CUADT_PERSONA==${codPersona}`;
          
          const responseCuadrantes = await axios.get(urlCuadrantes, {
            params: { rquery: rqueryCuadrantes },
            headers: getHeaders()
          });
          
          if (responseCuadrantes.data && responseCuadrantes.data.errorCode === '200') {
            let cuadrantes = [];
            if (responseCuadrantes.data.data && responseCuadrantes.data.data.items) {
              cuadrantes = responseCuadrantes.data.data.items;
            } else if (responseCuadrantes.data.data && Array.isArray(responseCuadrantes.data.data)) {
              cuadrantes = responseCuadrantes.data.data;
            }
            
            if (cuadrantes.length > 0) {
              console.log(`✅ Encontrados ${cuadrantes.length} cuadrantes para ${año}-${mes}`);
              resultados.cuadrantesEncontrados.push({
                año,
                mes,
                cantidad: cuadrantes.length,
                datos: cuadrantes
              });
            }
          }
          
          // Consultar detalles
          const urlDetalles = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes-detalles`;
          const rqueryDetalles = `CUADD_EMP==${empresa};CUADD_DELEG==${delegacion};CUADD_CTRT==${contrato};CUADD_SERV==${servicio};CUADD_CAL==${año};CUADD_MES==${mes}`;
          
          const responseDetalles = await axios.get(urlDetalles, {
            params: { rquery: rqueryDetalles },
            headers: getHeaders()
          });
          
          if (responseDetalles.data && responseDetalles.data.errorCode === '200') {
            let detalles = [];
            if (responseDetalles.data.data && responseDetalles.data.data.items) {
              detalles = responseDetalles.data.data.items;
            } else if (responseDetalles.data.data && Array.isArray(responseDetalles.data.data)) {
              detalles = responseDetalles.data.data;
            }
            
            if (detalles.length > 0) {
              console.log(`✅ Encontrados ${detalles.length} detalles para ${año}-${mes}`);
              resultados.detallesEncontrados.push({
                año,
                mes,
                cantidad: detalles.length,
                datos: detalles
              });
            }
          }
          
        } catch (error) {
          console.log(`⚠️ Error consultando ${año}-${mes}:`, error.message);
        }
      }
    }
    
    // Crear resumen
    resultados.resumen = {
      totalCuadrantes: resultados.cuadrantesEncontrados.length,
      totalDetalles: resultados.detallesEncontrados.length,
      añosConDatos: [...new Set([...resultados.cuadrantesEncontrados.map(c => c.año), ...resultados.detallesEncontrados.map(d => d.año)])],
      mesesConDatos: [...new Set([...resultados.cuadrantesEncontrados.map(c => `${c.año}-${c.mes}`), ...resultados.detallesEncontrados.map(d => `${d.año}-${d.mes}`)])],
      mejorCoincidencia: null
    };
    
    // Encontrar la mejor coincidencia
    const coincidencias = [];
    for (const cuadrante of resultados.cuadrantesEncontrados) {
      const detalle = resultados.detallesEncontrados.find(d => d.año === cuadrante.año && d.mes === cuadrante.mes);
      if (detalle) {
        coincidencias.push({
          año: cuadrante.año,
          mes: cuadrante.mes,
          cuadrantes: cuadrante.cantidad,
          detalles: detalle.cantidad
        });
      }
    }
    
    if (coincidencias.length > 0) {
      resultados.resumen.mejorCoincidencia = coincidencias[0];
    }
    
    console.log('📊 RESUMEN DE DATOS HISTÓRICOS:', resultados.resumen);
    return resultados;
    
  } catch (error) {
    console.error('❌ Error buscando datos históricos:', error);
    throw error;
  }
};

export const obtenerTurnosCuadrantesInteligente = async (empresa, delegacion, contrato, servicio, año, mes = null, dia = null, codPersona = null) => {
  try {
    console.log('🔍 BÚSQUEDA INTELIGENTE DE TURNOS...');
    console.log('Parámetros iniciales:', { empresa, delegacion, contrato, servicio, año, mes, dia, codPersona });
    
    // Primero intentar con el año solicitado
    let turnos = await obtenerTurnosCuadrantes(empresa, delegacion, contrato, servicio, año, mes, dia, codPersona);
    
    if (turnos && turnos.length > 0) {
      console.log(`✅ Encontrados ${turnos.length} turnos en el año ${año}`);
      return turnos;
    }
    
    console.log(`❌ No se encontraron turnos en ${año}. Buscando en años anteriores...`);
    
    // Si no hay datos, buscar en años anteriores
    const añosAnteriores = [año - 1, año - 2, año - 3];
    
    for (const añoAnterior of añosAnteriores) {
      if (añoAnterior < 2020) break; // No buscar antes de 2020
      
      console.log(`🔍 Buscando en ${añoAnterior}...`);
      
      try {
        turnos = await obtenerTurnosCuadrantes(empresa, delegacion, contrato, servicio, añoAnterior, mes, dia, codPersona);
        
        if (turnos && turnos.length > 0) {
          console.log(`✅ Encontrados ${turnos.length} turnos en ${añoAnterior}`);
          
          // Agregar información sobre el año real de los datos
          turnos.forEach(turno => {
            turno.añoReal = añoAnterior;
            turno.añoSolicitado = año;
          });
          
          return turnos;
        }
      } catch (error) {
        console.log(`⚠️ Error buscando en ${añoAnterior}:`, error.message);
      }
    }
    
    console.log('❌ No se encontraron turnos en ningún año');
    return [];
    
  } catch (error) {
    console.error('❌ Error en búsqueda inteligente:', error);
    throw error;
  }
};

// Función para hacer delay entre peticiones
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Hace push de los turnos guardados en Sentry a Freematica
 * @param {Array} turnos - Array de turnos guardados en Sentry
 * @param {Object} detallesServicio - Detalles del servicio
 * @returns {Promise<Object>} - Resultado del push a Freematica
 */
export const pushTurnosAFreematica = async (turnos, detallesServicio) => {
  try {
    console.log('🚀 Iniciando push a Freematica...');
    console.log('📊 Turnos a enviar:', turnos.length);
    
    if (!turnos || turnos.length === 0) {
      throw new Error('No hay turnos para enviar a Freematica');
    }

    // Extraer datos de localización del servicio
    const datosLocalizacion = extraerDatosLocalizacion(detallesServicio.localizacion);
    const empresa = datosLocalizacion.empresa || '0001';
    const delegacion = datosLocalizacion.delegacion || '06';
    const contrato = datosLocalizacion.contrato;
    const servicio = datosLocalizacion.servicio;

    // Buscar el servicio en Freematica para obtener los datos correctos
    console.log('🔍 Buscando servicio en Freematica para push...');
    const servicioFreematica = await buscarServicioFreematica(empresa, delegacion, contrato, servicio);
    
    console.log('✅ Servicio encontrado en Freematica:', servicioFreematica);

    // Agrupar turnos por persona y turno operativo
    const turnosAgrupados = {};
    
    turnos.forEach(turno => {
      const key = `${turno.vigilante}_${turno.turnoOper || 1}`;
      if (!turnosAgrupados[key]) {
        turnosAgrupados[key] = {
          cuadt_turnoper: turno.turnoOper || 1,
          cuadt_persona: turno.vigilante,
          cuadt_emp_perso: servicioFreematica.empresa,
          cuadt_deleg_perso: servicioFreematica.delegacion,
          Dias: []
        };
      }
      
      // Convertir fecha a día del mes
      const fecha = new Date(turno.fecha);
      const dia = fecha.getDate();
      
      // Calcular horas totales
      const horaInicio = turno.inicioTurno || turno.horaInicio;
      const horaFin = turno.finTurno || turno.horaFin;
      const horasTotales = turno.horasTotales || 8.0; // Default 8 horas
      
      turnosAgrupados[key].Dias.push({
        cuadd_dd: dia,
        cuadd_hhini: horaInicio,
        cuadd_hhfin: horaFin,
        cuadd_hhtotd: horasTotales,
        cuadd_tarea: "01" // Tarea por defecto
      });
    });

    // Convertir a array
    const turnosParaAPI = Object.values(turnosAgrupados);
    
    console.log('📋 Turnos agrupados para API:', turnosParaAPI);

    // Construir el IDReg: Empresa__Delegación__Calendario__Mes__Contrato__Servicio
    const año = new Date(turnos[0].fecha).getFullYear();
    const mes = String(new Date(turnos[0].fecha).getMonth() + 1).padStart(2, '0');
    
    const idRegCodificado = construirIdReg(
      servicioFreematica.empresa,
      servicioFreematica.delegacion,
      año,
      mes,
      servicioFreematica.contrato,
      servicioFreematica.servicio
    );
    
    console.log('🔍 IDReg (raw):', `${servicioFreematica.empresa}__${servicioFreematica.delegacion}__${año}__${mes}__${servicioFreematica.contrato}__${servicioFreematica.servicio}`);
    console.log('🔍 IDReg codificado:', idRegCodificado);

    // Hacer la petición PUT a Freematica
    const url = `${freematicaConfig.apiUrl}/pvss/v2/cuadrante/${idRegCodificado}`;
    
    console.log('🌐 URL de la petición:', url);
    console.log('📤 Body de la petición:', JSON.stringify(turnosParaAPI, null, 2));

    const response = await axios.put(url, turnosParaAPI, {
      headers: getHeaders(servicioFreematica.empresa)
    });

    console.log('📥 Respuesta de Freematica:', response.data);

    // Verificar si la actualización fue exitosa
    if (response.data && response.data.errorCode === '200') {
      const mensaje = response.data.data || '';
      
      if (mensaje.includes('Actualizado con éxito')) {
        console.log('✅ Push a Freematica exitoso');
        return {
          success: true,
          message: 'Turnos enviados exitosamente a Freematica',
          data: response.data
        };
      } else if (mensaje.includes('Cuadrante inexistente')) {
        throw new Error('El cuadrante no existe en Freematica. Debe crearse primero.');
      } else if (mensaje.includes('Personal no vinculado al Servicio')) {
        throw new Error('El personal no está vinculado al servicio en Freematica');
      } else if (mensaje.includes('personal sin contratacion') || mensaje.includes('personal sin contratación')) {
        throw new Error(
          'Personal sin contratación: El vigilante no tiene un registro de contratación activo en Freematica para esta empresa/servicio. ' +
          'Debe dar de alta la contratación del trabajador en Freematica (módulo de personal) y asignarlo al contrato/servicio correspondiente.'
        );
      } else if (mensaje.includes('Solap. con Incidencias')) {
        throw new Error('Hay solapamiento con incidencias en Freematica');
      } else if (mensaje.includes('Solap. propio cuad.')) {
        throw new Error('Hay solapamiento en el propio cuadrante en Freematica');
      } else if (mensaje.includes('Solap. otros cuad.')) {
        throw new Error('Hay solapamiento con otros cuadrantes en Freematica');
      } else if (mensaje.includes('Cuadrante NO Actualizado') || mensaje.includes('Proceso Cancelado')) {
        throw new Error(`Freematica rechazó la actualización: ${mensaje}`);
      } else {
        console.warn('⚠️ Respuesta inesperada de Freematica:', mensaje);
        return {
          success: true,
          message: 'Turnos enviados a Freematica (respuesta inesperada)',
          data: response.data
        };
      }
    } else {
      throw new Error(`Error en la respuesta de Freematica: ${response.data?.errorMessage || 'Error desconocido'}`);
    }

  } catch (error) {
    console.error('❌ Error en push a Freematica:', error);
    throw new Error(`Error enviando turnos a Freematica: ${error.message}`);
  }
};

/**
 * Guarda turnos en Sentry y hace push a Freematica
 * @param {Array} turnos - Array de turnos a guardar
 * @param {Object} detallesServicio - Detalles del servicio
 * @param {string} servicioId - ID del servicio
 * @returns {Promise<Object>} - Resultado del proceso completo
 */
export const guardarTurnosYPushAFreematica = async (turnos, detallesServicio, servicioId) => {
  try {
    console.log('🔄 Iniciando proceso completo: Guardar en Sentry + Push a Freematica');
    
    // Primero guardar en Sentry
    const resultadoSentry = await guardarTurnosEnBD(turnos, servicioId);
    console.log('✅ Turnos guardados en Sentry:', resultadoSentry);
    
    // Luego hacer push a Freematica
    const resultadoFreematica = await pushTurnosAFreematica(turnos, detallesServicio);
    console.log('✅ Push a Freematica completado:', resultadoFreematica);
    
    return {
      success: true,
      sentry: resultadoSentry,
      freematica: resultadoFreematica,
      message: 'Turnos guardados en Sentry y enviados a Freematica exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error en proceso completo:', error);
    throw error;
  }
};

/**
 * Obtiene los turnos existentes de un cuadrante en Freematica
 * @param {string} empresa - Código de la empresa
 * @param {string} delegacion - Código de la delegación
 * @param {string} calendario - Año del calendario
 * @param {string} mes - Mes del calendario
 * @param {string} contrato - Código del contrato
 * @param {string} servicio - Código del servicio
 * @returns {Promise<Array>} - Array de turnos existentes en formato Freematica
 */
export const obtenerTurnosExistentesFreematica = async (empresa, delegacion, calendario, mes, contrato, servicio) => {
  try {
    console.log('╔════════════════════════════════════════════════════════════╗');
    console.log('║   🔍 OBTENIENDO TURNOS EXISTENTES DE FREEMATICA           ║');
    console.log('╚════════════════════════════════════════════════════════════╝');
    console.log('📋 Parámetros recibidos:');
    console.log(`   • Empresa: ${empresa}`);
    console.log(`   • Delegación: ${delegacion}`);
    console.log(`   • Calendario (año): ${calendario}`);
    console.log(`   • Mes: ${mes}`);
    console.log(`   • Contrato: ${contrato}`);
    console.log(`   • Servicio: ${servicio}`);
    
    // Asegurar formato correcto del mes (01, 02, etc.)
    const mesFormateado = mes.toString().padStart(2, '0');
    console.log(`   • Mes formateado: ${mesFormateado}`);
    
    // Obtener turnos existentes usando la función existente
    console.log('\n🚀 Llamando a obtenerTurnosCuadrantes...');
    const turnosExistentes = await obtenerTurnosCuadrantes(
      empresa,
      delegacion,
      contrato,
      servicio,
      calendario,
      mesFormateado
    );
    
    console.log('\n📊 RESULTADO DE LA CONSULTA:');
    console.log(`   • Turnos encontrados: ${turnosExistentes ? turnosExistentes.length : 0}`);
    
    if (!turnosExistentes || turnosExistentes.length === 0) {
      console.log('⚠️ NO HAY TURNOS EXISTENTES');
      console.log('   • Esto puede significar:');
      console.log('     1. El cuadrante no existe aún en Freematica');
      console.log('     2. No hay turnos asignados para este mes');
      console.log('     3. Los parámetros de búsqueda no coinciden');
      console.log('✅ Se creará el cuadrante desde cero');
      return [];
    }
    
    console.log('\n✅ TURNOS EXISTENTES ENCONTRADOS:');
    console.log(`   • Total de registros: ${turnosExistentes.length}`);
    
    // Mostrar un resumen de los turnos encontrados
    const personasUnicas = [...new Set(turnosExistentes.map(t => t.CUADD_PERSONA))];
    console.log(`   • Vigilantes únicos: ${personasUnicas.length}`);
    console.log(`   • Códigos de persona: ${personasUnicas.join(', ')}`);
    
    const diasUnicos = [...new Set(turnosExistentes.map(t => t.CUADD_DD))].sort((a, b) => a - b);
    console.log(`   • Días con turnos: ${diasUnicos.join(', ')}`);
    
    // Agrupar turnos por persona y turno operativo
    console.log('\n🔀 Agrupando turnos por persona...');
    const turnosAgrupados = {};
    
    turnosExistentes.forEach((turno, index) => {
      const key = `${turno.CUADD_PERSONA}_${turno.CUADT_TURNOPER}`;
      
      if (!turnosAgrupados[key]) {
        turnosAgrupados[key] = {
          cuadt_turnoper: parseInt(turno.CUADT_TURNOPER),
          cuadt_persona: turno.CUADD_PERSONA.toString(),
          cuadt_emp_perso: turno.CUADT_EMP_PERSO || empresa,
          cuadt_deleg_perso: turno.CUADT_DELEG_PERSO || delegacion,
          Dias: []
        };
        console.log(`   ➕ Nuevo vigilante: ${turno.CUADD_PERSONA} (turno operativo ${turno.CUADT_TURNOPER})`);
      }
      
      // Agregar el día
      turnosAgrupados[key].Dias.push({
        cuadd_dd: parseInt(turno.CUADD_DD),
        cuadd_hhini: turno.CUADD_HHINI,
        cuadd_hhfin: turno.CUADD_HHFIN,
        cuadd_hhtotd: parseFloat(turno.CUADD_HHTOTD || 8.0),
        cuadd_tarea: turno.CUADD_TAREA || "01"
      });
    });
    
    const resultado = Object.values(turnosAgrupados);
    
    console.log('\n📦 TURNOS AGRUPADOS Y FORMATEADOS:');
    console.log(`   • Total de vigilantes: ${resultado.length}`);
    resultado.forEach((turno, idx) => {
      console.log(`   ${idx + 1}. Persona ${turno.cuadt_persona}: ${turno.Dias.length} días asignados`);
    });
    
    console.log('\n✅ Turnos existentes listos para fusionar');
    console.log('╚════════════════════════════════════════════════════════════╝\n');
    
    return resultado;
  } catch (error) {
    console.error('❌ Error obteniendo turnos existentes:', error);
    // Si no se pueden obtener los turnos existentes, devolver array vacío
    // Esto permite que continúe el proceso aunque Freematica no tenga turnos previos
    console.warn('⚠️ Continuando sin turnos existentes...');
    return [];
  }
};

/**
 * Obtiene TODOS los turnos con vigilante asignado desde FIRESTORE para un cuadrante específico
 * IMPORTANTE: Firestore es la fuente de verdad, no Freematica
 * @param {string} cuadranteId - ID del cuadrante en Firestore
 * @param {Object} currentService - Servicio actual con su ID
 * @param {string} mes - Mes a filtrar (opcional, formato "11" o "2025-11")
 * @param {Object} datosServicio - Datos del servicio {empresa, delegacion} (opcional)
 * @returns {Promise<Array>} - Array de turnos existentes en formato Freematica
 */
export const obtenerTurnosExistentesFirestore = async (cuadranteId, currentService, mes = null, datosServicio = {}) => {
  try {
    console.log('╔════════════════════════════════════════════════════════════╗');
    console.log('║   📥 OBTENIENDO TURNOS DESDE FIRESTORE                    ║');
    console.log('╚════════════════════════════════════════════════════════════╝');
    console.log('📋 Parámetros:');
    console.log(`   • Cuadrante ID: ${cuadranteId}`);
    console.log(`   • Servicio ID: ${currentService.id}`);
    console.log(`   • Filtro mes: ${mes || 'Todos'}`);
    
    // Importar Firestore
    const { db } = await import('../../../firebase');
    const { collection, getDocs, doc } = await import('firebase/firestore');
    
    // Referencia a la colección de turnos del cuadrante
    const cuadranteRef = doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId);
    const turnosRef = collection(cuadranteRef, 'turnos');
    
    console.log('\n🔍 Consultando Firestore...');
    const turnosSnapshot = await getDocs(turnosRef);
    
    console.log(`📊 Documentos encontrados: ${turnosSnapshot.size}`);
    
    if (turnosSnapshot.empty) {
      console.log('⚠️ NO HAY TURNOS EN FIRESTORE para este cuadrante');
      console.log('╚════════════════════════════════════════════════════════════╝\n');
      return [];
    }
    
    // Convertir documentos a array y filtrar
    const todosTurnos = [];
    turnosSnapshot.forEach(docSnap => {
      const turno = { id: docSnap.id, ...docSnap.data() };
      
      // Filtrar por mes si se especifica
      if (mes && turno.fecha) {
        const turnoMes = turno.fecha.substring(5, 7); // "2025-11-03" -> "11"
        const filtroMes = mes.toString().padStart(2, '0');
        
        if (turnoMes !== filtroMes) {
          return; // Skip este turno
        }
      }
      
      todosTurnos.push(turno);
    });
    
    console.log(`\n📊 Turnos después de filtro por mes: ${todosTurnos.length}`);
    
    // Mostrar una muestra de los turnos para debug
    if (todosTurnos.length > 0) {
      console.log('\n📋 Muestra de turnos encontrados:');
      todosTurnos.slice(0, 10).forEach((turno, idx) => {
        console.log(`   ${idx + 1}. ${turno.fecha} | Vigilante: "${turno.vigilante}" | sinAsignar: ${turno.sinAsignar} | vigilanteId: ${turno.vigilanteId}`);
      });
      if (todosTurnos.length > 10) {
        console.log(`   ... y ${todosTurnos.length - 10} turnos más`);
      }
    }
    
    // Filtrar solo turnos CON vigilante asignado
    const turnosConVigilante = todosTurnos.filter(turno => {
      const tieneVigilante = turno.vigilante && 
                            turno.vigilante.trim() !== '' && 
                            !turno.sinAsignar;
      
      if (!tieneVigilante) {
        console.log(`   ⏭️ Saltando turno sin vigilante: ${turno.fecha} | vigilante="${turno.vigilante}" | sinAsignar=${turno.sinAsignar}`);
      }
      
      return tieneVigilante;
    });
    
    console.log(`\n✅ Turnos CON vigilante asignado: ${turnosConVigilante.length}`);
    
    if (turnosConVigilante.length === 0) {
      console.log('⚠️ NO HAY TURNOS CON VIGILANTE ASIGNADO');
      console.log('╚════════════════════════════════════════════════════════════╝\n');
      return [];
    }
    
    // Mostrar resumen
    const vigilantesUnicos = [...new Set(turnosConVigilante.map(t => t.vigilante))];
    console.log(`\n📋 Resumen:`);
    console.log(`   • Vigilantes únicos: ${vigilantesUnicos.length}`);
    console.log(`   • Códigos: ${vigilantesUnicos.join(', ')}`);
    
    // Agrupar por persona para formato Freematica: 1 turno por persona con array Dias (varios días)
    // Según Freematica: "1 turno con desglose de N días", no "1 turno con 1 día, N veces"
    console.log('\n🔀 Agrupando para formato Freematica (1 turno por persona, Dias con desglose de días)...');
    const turnosAgrupados = {};

    turnosConVigilante.forEach(turno => {
      const personaId = turno.vigilante;
      const key = personaId.toString();

      const fecha = new Date(turno.fecha);
      const dia = fecha.getDate();

      if (!turnosAgrupados[key]) {
        const empPerso = turno._freematica_empresa || datosServicio.empresa || "0001";
        const delegPerso = turno._freematica_delegacion || datosServicio.delegacion || "06";
        turnosAgrupados[key] = {
          cuadt_persona: personaId.toString(),
          cuadt_emp_perso: empPerso,
          cuadt_deleg_perso: delegPerso,
          Dias: []
        };
        console.log(`   ➕ Vigilante: ${personaId} | empresa=${empPerso}, delegacion=${delegPerso}`);
      }

      let horasTotales = turno.horasTotales;
      if (!horasTotales && turno.inicioTurno && turno.finTurno) {
        try {
          const [hi, mi] = turno.inicioTurno.split(':').map(Number);
          const [hf, mf] = turno.finTurno.split(':').map(Number);
          let diff = (hf * 60 + mf) - (hi * 60 + mi);
          if (diff < 0) diff += 24 * 60;
          horasTotales = diff / 60;
        } catch (e) { horasTotales = 8.0; }
      }
      turnosAgrupados[key].Dias.push({
        cuadd_dd: dia,
        cuadd_hhini: turno.inicioTurno || '08:00',
        cuadd_hhfin: turno.finTurno || '16:00',
        cuadd_hhtotd: parseFloat(horasTotales || 8.0),
        cuadd_tarea: turno.tarea || "01"
      });
    });
    
    // Asignar cuadt_turnoper secuencial (1, 2, 3...) después de agrupar
    const resultado = Object.values(turnosAgrupados).map((turno, idx) => ({
      ...turno,
      cuadt_turnoper: idx + 1
    }));
    
    console.log('\n📦 RESULTADO FINAL:');
    console.log(`   • Total de vigilantes: ${resultado.length}`);
    resultado.forEach((turno, idx) => {
      const dias = turno.Dias.map(d => d.cuadd_dd).sort((a, b) => a - b).join(', ');
      console.log(`   ${idx + 1}. Persona ${turno.cuadt_persona}: ${turno.Dias.length} días [${dias}]`);
    });
    
    console.log('\n✅ Turnos listos para enviar a Freematica');
    console.log('╚════════════════════════════════════════════════════════════╝\n');
    
    return resultado;
  } catch (error) {
    console.error('❌ Error obteniendo turnos desde Firestore:', error);
    console.error('╚════════════════════════════════════════════════════════════╝\n');
    return [];
  }
};

/**
 * Fusiona turnos existentes con nuevos cambios
 * @param {Array} turnosExistentes - Turnos actuales en Freematica
 * @param {Array} turnosNuevos - Nuevos turnos a agregar/modificar
 * @returns {Array} - Array de turnos fusionados
 */
export const fusionarTurnos = (turnosExistentes, turnosNuevos) => {
  console.log('\n╔════════════════════════════════════════════════════════════╗');
  console.log('║             🔀 FUSIONANDO TURNOS                          ║');
  console.log('╚════════════════════════════════════════════════════════════╝');
  console.log('📊 ESTADO INICIAL:');
  console.log(`   • Turnos existentes en Freematica: ${turnosExistentes.length} vigilantes`);
  console.log(`   • Turnos nuevos/modificados: ${turnosNuevos.length} vigilantes`);
  
  if (turnosExistentes.length > 0) {
    console.log('\n📋 Vigilantes EXISTENTES:');
    turnosExistentes.forEach((t, idx) => {
      console.log(`   ${idx + 1}. Persona ${t.cuadt_persona}: ${t.Dias.length} días`);
    });
  }
  
  if (turnosNuevos.length > 0) {
    console.log('\n📋 Vigilantes NUEVOS/MODIFICADOS:');
    turnosNuevos.forEach((t, idx) => {
      console.log(`   ${idx + 1}. Persona ${t.cuadt_persona}: ${t.Dias.length} días`);
    });
  }
  
  // Crear un mapa de turnos existentes por persona_turnooper_dia
  const mapaExistentes = new Map();
  
  turnosExistentes.forEach(turno => {
    const persona = turno.cuadt_persona;
    const turnoOper = turno.cuadt_turnoper;
    
    turno.Dias.forEach(dia => {
      const key = `${persona}_${turnoOper}_${dia.cuadd_dd}`;
      mapaExistentes.set(key, { turno, dia });
    });
  });
  
  // Agregar/actualizar con los nuevos turnos
  turnosNuevos.forEach(turnoNuevo => {
    const persona = turnoNuevo.cuadt_persona;
    const turnoOper = turnoNuevo.cuadt_turnoper;
    
    turnoNuevo.Dias.forEach(diaNuevo => {
      const key = `${persona}_${turnoOper}_${diaNuevo.cuadd_dd}`;
      
      // Buscar si ya existe un turno para esta persona/turno/día
      let turnoExistente = turnosExistentes.find(
        t => t.cuadt_persona === persona && t.cuadt_turnoper === turnoOper
      );
      
      if (turnoExistente) {
        // Actualizar o agregar el día
        const diaExistente = turnoExistente.Dias.find(d => d.cuadd_dd === diaNuevo.cuadd_dd);
        
        if (diaExistente) {
          // Actualizar día existente
          console.log(`🔄 Actualizando día ${diaNuevo.cuadd_dd} para persona ${persona}`);
          Object.assign(diaExistente, diaNuevo);
        } else {
          // Agregar nuevo día a turno existente
          console.log(`➕ Agregando día ${diaNuevo.cuadd_dd} para persona ${persona}`);
          turnoExistente.Dias.push(diaNuevo);
        }
      } else {
        // Crear nuevo turno completo
        console.log(`➕ Creando nuevo turno para persona ${persona}`);
        turnosExistentes.push({ ...turnoNuevo, Dias: [diaNuevo] });
      }
      
      mapaExistentes.set(key, { turno: turnoExistente || turnoNuevo, dia: diaNuevo });
    });
  });
  
  console.log('\n📊 RESULTADO DE LA FUSIÓN:');
  console.log(`   • Total de vigilantes en el cuadrante: ${turnosExistentes.length}`);
  
  if (turnosExistentes.length > 0) {
    console.log('\n📋 LISTADO COMPLETO DE VIGILANTES A ENVIAR:');
    turnosExistentes.forEach((turno, idx) => {
      const dias = turno.Dias.map(d => d.cuadd_dd).sort((a, b) => a - b).join(', ');
      console.log(`   ${idx + 1}. Persona ${turno.cuadt_persona}:`);
      console.log(`      • Turno operativo: ${turno.cuadt_turnoper}`);
      console.log(`      • Días asignados: ${turno.Dias.length} [${dias}]`);
    });
  }
  
  console.log('\n✅ Fusión completada exitosamente');
  console.log('╚════════════════════════════════════════════════════════════╝\n');
  
  return turnosExistentes;
};

/**
 * Actualiza un cuadrante en Freematica usando el endpoint PUT pvss/v2/cuadrante/{IdReg}
 * IMPORTANTE: Esta función fusiona los turnos nuevos con los existentes para no perder datos
 * @param {Object} cuadranteData - Datos del cuadrante a actualizar
 * @param {string} cuadranteData.empresa - Código de la empresa (ej: "0001")
 * @param {string} cuadranteData.delegacion - Código de la delegación (ej: "06")
 * @param {string} cuadranteData.calendario - Año del calendario (ej: "2025")
 * @param {string} cuadranteData.mes - Mes del calendario (ej: "10")
 * @param {string} cuadranteData.contrato - Código del contrato (ej: "13550")
 * @param {string} cuadranteData.servicio - Código del servicio (ej: "03")
 * @param {Array} cuadranteData.turnos - Array de turnos con la estructura requerida
 * @returns {Promise<Object>} - Respuesta de la API de Freematica
 */
export const actualizarCuadranteFreematica = async (cuadranteData) => {
  try {
    const { empresa, delegacion, calendario, mes, contrato, servicio, turnos } = cuadranteData;
    
    console.log('\n╔════════════════════════════════════════════════════════════╗');
    console.log('║   🚀 ACTUALIZANDO CUADRANTE EN FREEMATICA (PUT)          ║');
    console.log('╚════════════════════════════════════════════════════════════╝');
    console.log(`📋 Cuadrante: ${empresa}/${delegacion}/${calendario}/${mes}/${contrato}/${servicio}`);
    
    // Validar datos requeridos
    if (!empresa || !delegacion || !calendario || !mes || !contrato || !servicio) {
      throw new Error('Faltan datos requeridos: empresa, delegacion, calendario, mes, contrato, servicio');
    }
    
    if (!turnos || !Array.isArray(turnos) || turnos.length === 0) {
      throw new Error('Se requiere al menos un turno para actualizar el cuadrante');
    }
    
    console.log(`📊 Turnos a enviar: ${turnos.length} vigilantes`);
    
    // Los turnos YA VIENEN EN FORMATO FREEMATICA desde obtenerTurnosExistentesFirestore
    // Solo necesitamos validar que tengan la estructura correcta
    console.log('\n✅ Validando estructura de turnos...');
    turnos.forEach((turno, idx) => {
      if (!turno.cuadt_persona || !turno.Dias || !Array.isArray(turno.Dias)) {
        throw new Error(`Turno ${idx + 1} inválido: falta cuadt_persona o Dias`);
      }
      console.log(`   ${idx + 1}. Persona ${turno.cuadt_persona}: ${turno.Dias.length} días`);
    });
    
    // Construir IdReg y URL
    console.log('\n🔧 Construyendo petición PUT...');
    const idReg = `${empresa}__${delegacion}__${calendario}__${mes}__${contrato}__${servicio}`;
    const idRegEncoded = btoa(idReg);
    const url = `${freematicaConfig.apiUrl}/pvss/v2/cuadrante/${idRegEncoded}`;
    
    console.log(`📝 IDReg: ${idReg}`);
    console.log(`🔐 IDReg codificado: ${idRegEncoded}`);
    console.log(`🌐 URL: ${url}`);
    console.log(`📤 Body a enviar (${turnos.length} turnos):`, JSON.stringify(turnos, null, 2));
    
    // Realizar la petición PUT
    console.log('\n🚀 Enviando PUT a Freematica...');
    const response = await axios.put(url, turnos, {
      headers: getHeaders(empresa),
      timeout: 30000 // 30 segundos de timeout
    });
    
    console.log('📥 Respuesta de Freematica:');
    console.log(`   • Error Code: ${response.data.errorCode}`);
    console.log(`   • Error Message: ${response.data.errorMessage}`);
    
    // Formatear el campo 'data' para mejor legibilidad
    if (response.data.data) {
      const dataStr = response.data.data;
      // Dividir por corchetes y formatear
      const mensajes = dataStr
        .split('[')
        .filter(m => m.trim())
        .map(m => m.replace(/\]$/, '').trim());
      
      console.log('   • Data:');
      mensajes.forEach((msg, idx) => {
        console.log(`      ${idx + 1}. ${msg}`);
      });
    }
    
    if (response.data && response.data.errorCode === '200') {
      // IMPORTANTE: Freematica siempre devuelve errorCode 200, los errores vienen dentro de "data"
      const dataStr = response.data.data || '';
      
      // Detectar errores específicos dentro del campo data
      if (dataStr.includes('Cuadrante inexistente')) {
        console.error('\n❌ ERROR: CUADRANTE NO EXISTE EN FREEMATICA');
        console.error('💡 El cuadrante debe crearse primero en Freematica antes de poder actualizarlo');
        console.error(`📋 Cuadrante: ${empresa}/${delegacion}/${calendario}/${mes}/${contrato}/${servicio}`);
        console.error('╚════════════════════════════════════════════════════════════╝\n');
        return {
          success: false,
          data: dataStr,
          message: 'Cuadrante inexistente en Freematica. Debe crearse primero.',
          turnosEnviados: 0,
          errorTipo: 'cuadrante_inexistente'
        };
      }
      
      if (dataStr.includes('Personal no vinculado al Servicio')) {
        console.error('\n❌ ERROR: PERSONAL NO VINCULADO AL SERVICIO');
        console.error('╚════════════════════════════════════════════════════════════╝\n');
        return {
          success: false,
          data: dataStr,
          message: 'Personal no vinculado al servicio en Freematica',
          turnosEnviados: 0,
          errorTipo: 'personal_no_vinculado'
        };
      }
      
      if (dataStr.includes('Personal sin contratación') || dataStr.includes('personal sin contratacion')) {
        console.error('\n❌ ERROR: PERSONAL SIN CONTRATACIÓN');
        console.error('💡 Verificar que cuadt_emp_perso sea la empresa correcta del contrato/servicio');
        console.error(`📋 Empresa usada en turnos: ${turnos.map(t => t.cuadt_emp_perso).join(', ')}`);
        console.error(`📋 Empresa del IDReg: ${empresa}`);
        console.error('╚════════════════════════════════════════════════════════════╝\n');
        return {
          success: false,
          data: dataStr,
          message: 'Personal sin contratación en Freematica. Verificar empresa del personal.',
          turnosEnviados: 0,
          errorTipo: 'personal_sin_contratacion'
        };
      }
      
      if (dataStr.includes('Solap. con Incidencias')) {
        console.error('\n❌ ERROR: SOLAPAMIENTO CON INCIDENCIAS');
        return { success: false, data: dataStr, message: 'Solapamiento con incidencias', turnosEnviados: 0, errorTipo: 'solapamiento_incidencias' };
      }
      
      if (dataStr.includes('Solap. propio cuad.')) {
        console.error('\n❌ ERROR: SOLAPAMIENTO EN PROPIO CUADRANTE');
        return { success: false, data: dataStr, message: 'Solapamiento en el propio cuadrante', turnosEnviados: 0, errorTipo: 'solapamiento_propio' };
      }
      
      if (dataStr.includes('Solap. otros cuad.')) {
        console.error('\n❌ ERROR: SOLAPAMIENTO CON OTROS CUADRANTES');
        return { success: false, data: dataStr, message: 'Solapamiento con otros cuadrantes', turnosEnviados: 0, errorTipo: 'solapamiento_otros' };
      }
      
      const tieneErrores = dataStr.includes('Cuadrante NO Actualizado') || (dataStr.includes('Proceso Cancelado') && !dataStr.includes('Actualizado con éxito'));
      
      if (tieneErrores) {
        console.warn('\n⚠️ CUADRANTE CON ERRORES NO CATALOGADOS');
        console.log('╚════════════════════════════════════════════════════════════╝\n');
        return {
          success: false,
          data: dataStr,
          message: dataStr,
          turnosEnviados: 0,
          errorTipo: 'error_generico'
        };
      }
      
      // Si llegamos aquí, fue exitoso
      console.log('\n✅ CUADRANTE ACTUALIZADO EXITOSAMENTE');
      console.log('╚════════════════════════════════════════════════════════════╝\n');
      
      return {
        success: true,
        data: dataStr,
        message: 'Cuadrante actualizado exitosamente en Freematica',
        turnosEnviados: turnos.length
      };
    } else {
      const mensaje = response.data?.errorMessage || response.data?.data || 'Error desconocido de Freematica';
      throw new Error(mensaje);
    }
    
  } catch (error) {
    console.error('❌ Error al actualizar cuadrante en Freematica:', error);
    throw error;
  }
};

/**
 * Función auxiliar para calcular horas totales entre dos horarios
 * @param {string} horaInicio - Hora de inicio (formato HH:MM)
 * @param {string} horaFin - Hora de fin (formato HH:MM)
 * @returns {number} - Horas totales como número decimal
 */
const calcularHorasTotales = (horaInicio, horaFin) => {
  try {
    const [horaIni, minIni] = horaInicio.split(':').map(Number);
    const [horaFinNum, minFin] = horaFin.split(':').map(Number);
    
    const minutosInicio = horaIni * 60 + minIni;
    const minutosFin = horaFinNum * 60 + minFin;
    
    // Manejar turnos que cruzan medianoche
    let diferenciaMinutos = minutosFin - minutosInicio;
    if (diferenciaMinutos < 0) {
      diferenciaMinutos += 24 * 60; // Agregar 24 horas
    }
    
    return diferenciaMinutos / 60; // Convertir a horas
  } catch (error) {
    console.error('Error calculando horas totales:', error);
    return 8; // Valor por defecto
  }
};

/**
 * Obtiene cuadrantes ya formados por persona
 * @param {string} codPersona - Código de la persona
 * @param {string} empresa - Código de empresa (opcional, por defecto 0001)
 * @param {string} delegacion - Código de delegación (opcional)
 * @param {string} contrato - Código del contrato (opcional)
 * @param {string} servicio - Código del servicio (opcional)
 * @param {string} año - Año para filtrar (opcional)
 * @param {string} mes - Mes para filtrar (opcional)
 * @returns {Promise<Object>} - Promesa con los datos de los cuadrantes
 */
export const obtenerCuadrantesPorPersona = async (codPersona, empresa = '0001', delegacion = null, contrato = null, servicio = null, año = null, mes = null) => {
  try {
    console.log('📋 Obteniendo cuadrantes por persona...');
    console.log('Parámetros:', { codPersona, empresa, delegacion, contrato, servicio, año, mes });

    // Para búsqueda por persona, usamos el endpoint /cuadrantes (resumen mensual)
    // porque /cuadrantes-detalles NO tiene campo de persona
    const url = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes`;
    
    // Construir rquery para cuadrantes (usa CUADT_* )
    // Formato base: CUADT_PERSONA==12189
    let rquery = `CUADT_PERSONA==${codPersona}`;
    
    // Agregar delegación si se proporciona
    if (delegacion) {
      rquery += `;CUADT_DELEG==${delegacion}`;
    }

    // Agregar contrato si se proporciona
    if (contrato) {
      rquery += `;CUADT_CTRT==${contrato}`;
    }

    // Agregar servicio si se proporciona (con padding de ceros)
    if (servicio) {
      const servicioPadded = servicio.toString().padStart(2, '0');
      rquery += `;CUADT_SERV==${servicioPadded}`;
    }

    // Agregar año si se proporciona
    if (año) {
      rquery += `;CUADT_CAL==${año}`;
    }

    // Agregar mes si se proporciona
    if (mes) {
      rquery += `;CUADT_MES==${mes}`;
    }

    const params = {
      rquery: rquery
    };

    console.log('📡 === PETICIÓN POR PERSONA ===');
    console.log('URL base:', url);
    console.log('Query (sin encoding):', rquery);
    console.log('🌐 URL COMPLETA:', `${url}?rquery=${encodeURIComponent(rquery)}`);
    console.log('===============================');

    const response = await axios.get(url, {
      params,
      headers: getHeaders()
    });

    console.log('📥 === RESPUESTA ===');
    console.log('ErrorCode:', response.data.errorCode);
    console.log('📊 Total:', response.data.data?.total || 'N/A');
    console.log('📋 Items:', response.data.data?.items?.length || 0);
    console.log('===================');

    if (response.data && response.data.errorCode === '200') {
      console.log('✅ Cuadrantes obtenidos correctamente');
      return response.data;
    } else {
      console.error('❌ Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener cuadrantes por persona');
    }
  } catch (error) {
    console.error('❌ Error al obtener cuadrantes por persona:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

/**
 * Obtiene cuadrantes ya formados por contrato/servicio
 * @param {string} empresa - Código de empresa (opcional, por defecto 0001)
 * @param {string} delegacion - Código de delegación (opcional)
 * @param {string} contrato - Código del contrato
 * @param {string} servicio - Código del servicio
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar
 * @returns {Promise<Object>} - Promesa con los datos de los cuadrantes
 */
/**
 * Combina datos de cuadrantes (personas) con cuadrantes-detalles (días)
 * @param {string} empresa - Código de empresa
 * @param {string} delegacion - Código de delegación
 * @param {string} contrato - Código del contrato
 * @param {string} servicio - Código del servicio
 * @param {string} año - Año para filtrar
 * @param {string} mes - Mes para filtrar
 * @returns {Promise<Object>} - Promesa con los datos combinados
 */
export const obtenerCuadrantesConPersonas = async (empresa = '0001', delegacion = null, contrato, servicio, año, mes) => {
  try {
    console.log('📋🔀 Obteniendo cuadrantes combinados (personas + días)...');
    console.log('Parámetros:', { empresa, delegacion, contrato, servicio, año, mes });

    // 1. Obtener personas asignadas de /cuadrantes
    const urlCuadrantes = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes`;
    const servicioPadded = servicio ? servicio.toString().padStart(2, '0') : servicio;
    // ✅ Incluir empresa en la query FIQL
    let rqueryCuadrantes = `CUADT_EMP==${empresa};CUADT_CTRT==${contrato};CUADT_SERV==${servicioPadded};CUADT_CAL==${año};CUADT_MES==${mes}`;
    if (delegacion) {
      rqueryCuadrantes += `;CUADT_DELEG==${delegacion}`;
    }

    console.log('🔄 1/2: Obteniendo personas de /cuadrantes...');
    console.log(`   Query: ${rqueryCuadrantes}`);
    const responseCuadrantes = await axios.get(urlCuadrantes, {
      params: { rquery: rqueryCuadrantes },
      headers: getHeaders() // Header auth siempre con 0001
    });

    // 2. Obtener días específicos de /cuadrantes-detalles
    const urlDetalles = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes-detalles`;
    // ✅ Incluir empresa en la query FIQL
    let rqueryDetalles = `CUADD_EMP==${empresa};CUADD_CTRT==${contrato};CUADD_SERV==${servicioPadded};CUADD_CAL==${año};CUADD_MES==${mes}`;
    if (delegacion) {
      rqueryDetalles += `;CUADD_DELEG==${delegacion}`;
    }

    console.log('🔄 2/2: Obteniendo días de /cuadrantes-detalles...');
    console.log(`   Query: ${rqueryDetalles}`);
    const responseDetalles = await axios.get(urlDetalles, {
      params: { rquery: rqueryDetalles },
      headers: getHeaders() // Header auth siempre con 0001
    });

    // 3. Combinar los datos
    const personas = responseCuadrantes.data?.data?.items || [];
    const detalles = responseDetalles.data?.data?.items || [];

    console.log('✅ Datos obtenidos:', {
      personas: personas.length,
      detalles: detalles.length
    });

    // 4. Crear un mapa de personas por turno
    const personasPorTurno = {};
    personas.forEach(p => {
      const key = `${p.CUADT_EMP}_${p.CUADT_DELEG}_${p.CUADT_CTRT}_${p.CUADT_SERV}_${p.CUADT_CAL}_${p.CUADT_MES}_${p.CUADT_TURNOPER}`;
      if (!personasPorTurno[key]) {
        personasPorTurno[key] = [];
      }
      personasPorTurno[key].push(p);
    });

    // 5. Agregar información de persona a cada detalle
    const detallesConPersonas = detalles.map(d => {
      const key = `${d.CUADD_EMP}_${d.CUADD_DELEG}_${d.CUADD_CTRT}_${d.CUADD_SERV}_${d.CUADD_CAL}_${d.CUADD_MES}_${d.CUADD_TURNOPER}`;
      const personasDelTurno = personasPorTurno[key] || [];
      
      // Si hay múltiples personas en el turno, clonar el detalle para cada persona
      if (personasDelTurno.length > 0) {
        return personasDelTurno.map(persona => ({
          ...d,
          // Agregar campos de persona
          CUADT_PERSONA: persona.CUADT_PERSONA,
          VSSPER_NOM: persona.VSSPER_NOM,
          VSSPER_APELL1: persona.VSSPER_APELL1,
          VSSPER_APELL2: persona.VSSPER_APELL2,
          VSSPER_NIF: persona.VSSPER_NIF,
          VSSPER_NIF_DC: persona.VSSPER_NIF_DC,
          // Mantener totales del mes de la persona
          CUADT_HHTOT: persona.CUADT_HHTOT,
          CUADT_HHTOTB: persona.CUADT_HHTOTB
        }));
      }
      return d; // Si no hay persona, devolver el detalle sin persona
    }).flat();

    console.log('✅ Datos combinados:', detallesConPersonas.length, 'registros');

    return {
      errorCode: '200',
      errorMessage: '',
      data: {
        total: detallesConPersonas.length.toString(),
        items: detallesConPersonas
      }
    };
  } catch (error) {
    console.error('❌ Error al obtener cuadrantes combinados:', error);
    throw error;
  }
};

export const obtenerCuadrantesPorContrato = async (empresa = '0001', delegacion = null, contrato, servicio, año, mes) => {
  try {
    console.log('📋 Obteniendo cuadrantes por contrato (días concretos)...');
    console.log('Parámetros:', { empresa, delegacion, contrato, servicio, año, mes });

    // Usar la nueva función combinada
    return await obtenerCuadrantesConPersonas(empresa, delegacion, contrato, servicio, año, mes);
  } catch (error) {
    console.error('❌ Error al obtener cuadrantes por contrato:', error);
    throw error;
  }
};

// Función legacy para obtener solo detalles sin personas
export const obtenerCuadrantesDetallesSolo = async (empresa = '0001', delegacion = null, contrato, servicio, año, mes) => {
  try {
    console.log('📋 Obteniendo cuadrantes-detalles (sin personas)...');
    console.log('Parámetros:', { empresa, delegacion, contrato, servicio, año, mes });

    const url = `${freematicaConfig.apiUrl}/pvss/v1/cuadrantes-detalles`;
    
    // Asegurar que el servicio tenga formato correcto (pad con ceros si es necesario)
    const servicioPadded = servicio ? servicio.toString().padStart(2, '0') : servicio;
    
    // Construir rquery para cuadrantes-detalles (usa CUADD_* en lugar de CUADT_*)
    // ✅ Incluir empresa en la query FIQL
    // Formato: CUADD_EMP==0040;CUADD_CTRT==11384;CUADD_SERV==01;CUADD_CAL==2016;CUADD_MES==6
    let rquery = `CUADD_EMP==${empresa};CUADD_CTRT==${contrato};CUADD_SERV==${servicioPadded};CUADD_CAL==${año};CUADD_MES==${mes}`;
    
    // Agregar delegación si se proporciona
    if (delegacion) {
      rquery += `;CUADD_DELEG==${delegacion}`;
    }

    const params = {
      rquery: rquery
    };

    console.log('📡 === PETICIÓN POR CONTRATO ===');
    console.log('URL base:', url);
    console.log('Query (sin encoding):', rquery);
    console.log('🌐 URL COMPLETA:', `${url}?rquery=${encodeURIComponent(rquery)}`);
    console.log('================================');

    const response = await axios.get(url, {
      params,
      headers: getHeaders() // Header auth siempre con 0001
    });

    console.log('📥 === RESPUESTA ===');
    console.log('ErrorCode:', response.data.errorCode);
    console.log('📊 Total:', response.data.data?.total || 'N/A');
    console.log('📋 Items:', response.data.data?.items?.length || 0);
    console.log('===================');

    if (response.data && response.data.errorCode === '200') {
      console.log('✅ Cuadrantes obtenidos correctamente');
      return response.data;
    } else {
      console.error('❌ Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener cuadrantes por contrato');
    }
  } catch (error) {
    console.error('❌ Error al obtener cuadrantes por contrato:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

/**
 * Obtiene los detalles de un cuadrante específico para visualización
 * @param {string} idReg - ID del registro del cuadrante
 * @returns {Promise<Object>} - Promesa con los detalles del cuadrante
 */
export const obtenerDetalleCuadrante = async (idReg) => {
  try {
    console.log('📋 Obteniendo detalle del cuadrante...');
    console.log('ID Registro:', idReg);

    const url = `${freematicaConfig.apiUrl}/pvss/v1/cuadrante/${idReg}`;

    console.log('URL:', url);

    const response = await axios.get(url, {
      headers: getHeaders()
    });

    console.log('Respuesta de la API:', response.data);

    if (response.data && response.data.errorCode === '200') {
      console.log('✅ Detalle del cuadrante obtenido correctamente');
      return response.data;
    } else {
      console.error('❌ Error en la respuesta:', response.data);
      throw new Error(response.data.errorMessage || 'Error al obtener detalle del cuadrante');
    }
  } catch (error) {
    console.error('❌ Error al obtener detalle del cuadrante:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

// ====================================================================
// FUNCIONES PARA GESTIÓN DE CONTRATOS Y SERVICIOS EN FREEMATICA
// ====================================================================

/**
 * Mapa de provincias españolas a códigos numéricos
 * Usado para convertir nombres de provincia a códigos de delegación en Freematica
 */
const PROVINCIAS_CODIGOS = {
  // Andalucía
  'Almería': '04', 'Cádiz': '11', 'Córdoba': '14', 'Granada': '18',
  'Huelva': '21', 'Jaén': '23', 'Málaga': '29', 'Sevilla': '41',
  // Aragón
  'Huesca': '22', 'Teruel': '44', 'Zaragoza': '50',
  // Asturias
  'Asturias': '33',
  // Baleares
  'Baleares': '07', 'Islas Baleares': '07',
  // Canarias
  'Las Palmas': '35', 'Santa Cruz de Tenerife': '38',
  // Cantabria
  'Cantabria': '39',
  // Castilla-La Mancha
  'Albacete': '02', 'Ciudad Real': '13', 'Cuenca': '16',
  'Guadalajara': '19', 'Toledo': '45',
  // Castilla y León
  'Ávila': '05', 'Burgos': '09', 'León': '24', 'Palencia': '34',
  'Salamanca': '37', 'Segovia': '40', 'Soria': '42', 'Valladolid': '47', 'Zamora': '49',
  // Cataluña
  'Barcelona': '08', 'Girona': '17', 'Lleida': '25', 'Tarragona': '43',
  // Comunidad Valenciana
  'Alicante': '03', 'Castellón': '12', 'Valencia': '46',
  // Extremadura
  'Badajoz': '06', 'Cáceres': '10',
  // Galicia
  'A Coruña': '15', 'La Coruña': '15', 'Lugo': '27', 'Ourense': '32', 'Pontevedra': '36',
  // Madrid
  'Madrid': '28',
  // Murcia
  'Murcia': '30',
  // Navarra
  'Navarra': '31',
  // País Vasco
  'Álava': '01', 'Vizcaya': '48', 'Guipúzcoa': '20',
  // La Rioja
  'La Rioja': '26',
  // Ceuta y Melilla
  'Ceuta': '51', 'Melilla': '52'
};

/**
 * Obtiene el código de provincia a partir del nombre
 * @param {string} nombreProvincia - Nombre de la provincia
 * @returns {string} - Código de provincia (2 dígitos) o '00' si no se encuentra
 */
export const obtenerCodigoProvincia = (nombreProvincia) => {
  if (!nombreProvincia) return '00';
  
  // Buscar coincidencia exacta primero
  if (PROVINCIAS_CODIGOS[nombreProvincia]) {
    return PROVINCIAS_CODIGOS[nombreProvincia];
  }
  
  // Buscar coincidencia parcial (ignorando acentos y mayúsculas)
  const nombreNormalizado = nombreProvincia.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  
  for (const [provincia, codigo] of Object.entries(PROVINCIAS_CODIGOS)) {
    const provinciaNormalizada = provincia.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    if (provinciaNormalizada === nombreNormalizado || 
        provinciaNormalizada.includes(nombreNormalizado) ||
        nombreNormalizado.includes(provinciaNormalizada)) {
      return codigo;
    }
  }
  
  console.warn(`⚠️ No se encontró código para la provincia: ${nombreProvincia}`);
  return '00';
};

/**
 * Obtiene la lista de contratos desde Freematica
 * @param {Object} options - Opciones de filtrado
 * @param {string} options.codEmpresa - Código de empresa (opcional)
 * @param {string} options.codDelegacion - Código de delegación (opcional)
 * @param {string} options.orden - Tipo de orden ASC/DESC (opcional)
 * @param {string} options.q - Valor del filtro (opcional)
 * @param {string} options.fq - Columnas a filtrar separadas por coma (opcional)
 * @param {number} options.items - Items por página (opcional)
 * @param {number} options.page - Número de página (opcional)
 * @param {string} options.columns - Columnas a devolver separadas por coma (opcional)
 * @returns {Promise<Object>} - Lista de contratos
 */
export const obtenerListaContratos = async (options = {}) => {
  try {
    console.log('📋 Obteniendo lista de contratos de Freematica...');
    console.log('Opciones:', options);
    
    const url = `${freematicaConfig.apiUrl}/pvss/v1/contratos`;
    const params = {};
    
    // Agregar parámetros opcionales
    if (options.codEmpresa) params.codEmpresa = options.codEmpresa;
    if (options.codDelegacion) params.codDelegacion = options.codDelegacion;
    if (options.orden) params.orden = options.orden;
    if (options.q) params.q = options.q;
    if (options.fq) params.fq = options.fq;
    if (options.items) params.items = options.items;
    if (options.page) params.page = options.page;
    if (options.columns) params.columns = options.columns;
    
    console.log('🌐 URL:', url);
    console.log('📤 Params:', params);
    
    const response = await axios.get(url, {
      params,
      headers: getHeaders()
    });
    
    console.log('📥 Respuesta:', response.data);
    
    if (response.data && response.data.errorCode === '200') {
      console.log(`✅ Se obtuvieron ${response.data.data?.items?.length || 0} contratos`);
      return {
        success: true,
        data: response.data.data,
        items: response.data.data?.items || [],
        total: response.data.data?.total || 0
      };
    } else {
      throw new Error(response.data?.errorMessage || 'Error al obtener contratos');
    }
  } catch (error) {
    console.error('❌ Error al obtener lista de contratos:', error);
    if (error.response) {
      console.error('Detalles:', {
        status: error.response.status,
        data: error.response.data
      });
    }
    throw error;
  }
};

/**
 * Busca un contrato específico por su código
 * @param {string} codigoContrato - Código del contrato a buscar
 * @param {string} codEmpresa - Código de empresa (opcional)
 * @returns {Promise<Object>} - Información del contrato encontrado
 */
export const buscarContratoPorCodigo = async (codigoContrato, codEmpresa = null) => {
  try {
    console.log(`🔍 Buscando contrato: ${codigoContrato}`);
    
    const contratos = await obtenerListaContratos({
      codEmpresa,
      q: codigoContrato,
      fq: 'CTRT_COD'
    });
    
    if (contratos.items && contratos.items.length > 0) {
      // Buscar coincidencia exacta
      const contratoExacto = contratos.items.find(c => c.CTRT_COD === codigoContrato);
      
      if (contratoExacto) {
        console.log('✅ Contrato encontrado:', contratoExacto);
        return {
          success: true,
          contrato: contratoExacto,
          idReg: contratoExacto.idReg // El idReg se usa para crear servicios
        };
      }
    }
    
    console.warn(`⚠️ No se encontró el contrato: ${codigoContrato}`);
    return {
      success: false,
      contrato: null,
      idReg: null
    };
  } catch (error) {
    console.error('❌ Error buscando contrato:', error);
    throw error;
  }
};

/**
 * Crea un nuevo servicio en un contrato existente de Freematica
 * @param {string} idRegContrato - ID del registro del contrato (obtenido de buscarContratoPorCodigo)
 * @param {Object} servicioData - Datos del servicio a crear
 * @param {string} servicioData.empresa - Código de empresa (ej: "0001")
 * @param {string} servicioData.delegacion - Código de delegación (ej: "28" para Madrid)
 * @param {string} servicioData.contrato - Código del contrato (ej: "13550")
 * @param {string} servicioData.codigo - Código del servicio (ej: "01")
 * @param {string} servicioData.descripcion - Descripción del servicio
 * @param {string} servicioData.inspector - Código del inspector (opcional)
 * @param {string} servicioData.tipo - Tipo de servicio (opcional)
 * @param {string} servicioData.fechaAlta - Fecha de alta formato YYYY-MM-DD
 * @param {string} servicioData.fechaFin - Fecha de fin formato YYYY-MM-DD (opcional)
 * @param {number} servicioData.precioEspecial - Precio especial (opcional, default 0.0)
 * @param {string} servicioData.nombreProducto - Nombre del producto (opcional)
 * @param {string} servicioData.situacion - Situación del servicio (opcional)
 * @param {string} servicioData.calendarioFestivo - Código de calendario festivo (opcional)
 * @param {string} servicioData.secuencial - Secuencial (opcional)
 * @param {string} servicioData.clase - Clase de servicio (opcional)
 * @param {number} servicioData.mesIncremento - Mes de incremento (opcional, default 0)
 * @param {string} servicioData.observacionesPuesto - Observaciones del puesto (opcional)
 * @param {string} servicioData.controlModificacion - Control de modificación (opcional)
 * @param {string} servicioData.motivoBaja - Motivo de baja (opcional)
 * @returns {Promise<Object>} - Resultado de la creación
 */
export const crearServicioEnContrato = async (idRegContrato, servicioData) => {
  try {
    console.log('\n╔════════════════════════════════════════════════════════════╗');
    console.log('║   🏢 CREANDO SERVICIO EN FREEMATICA                        ║');
    console.log('╚════════════════════════════════════════════════════════════╝');
    console.log('📋 ID Registro Contrato:', idRegContrato);
    console.log('📋 Datos del servicio:', servicioData);
    
    if (!idRegContrato) {
      throw new Error('Se requiere el ID del registro del contrato (idRegContrato)');
    }
    
    // Validar datos mínimos requeridos
    if (!servicioData.codigo) {
      throw new Error('Se requiere el código del servicio');
    }
    if (!servicioData.descripcion) {
      throw new Error('Se requiere la descripción del servicio');
    }
    
    // Construir el body del servicio
    const servicioBody = [{
      CTRTS_EMP: servicioData.empresa || freematicaConfig.company,
      CTRTS_DELEG: servicioData.delegacion || '',
      CTRTS_CTRT: servicioData.contrato || '',
      CTRTS_COD: servicioData.codigo.toString().padStart(2, '0'), // Asegurar formato XX
      CTRTS_DES: servicioData.descripcion,
      CTRTS_INSP: servicioData.inspector || '',
      CTRTS_TIPO: servicioData.tipo || '',
      CTRTS_FECALTA: servicioData.fechaAlta || new Date().toISOString().split('T')[0],
      CTRTS_FECFIN: servicioData.fechaFin || '',
      CTRTS_PRECIO_ESP: servicioData.precioEspecial || 0.0,
      CTRTS_NOMPROD: servicioData.nombreProducto || '',
      CTRTS_SITUACION: servicioData.situacion || '',
      CTRTS_CALFEST: servicioData.calendarioFestivo || '',
      CTRTS_SECUENCIAL: servicioData.secuencial || '',
      CTRTS_CLASE: servicioData.clase || '',
      CTRTS_MES_INCRE: servicioData.mesIncremento || 0,
      CTRTS_OBS_PUESTO: servicioData.observacionesPuesto || '',
      CTRTS_CONTROL_MODIF: servicioData.controlModificacion || '',
      CTRTS_MOTIVO_BAJA: servicioData.motivoBaja || ''
    }];
    
    const url = `${freematicaConfig.apiUrl}/pvss/v2/contratos/${idRegContrato}/servicios`;
    
    console.log('🌐 URL:', url);
    console.log('📤 Body:', JSON.stringify(servicioBody, null, 2));
    
    const response = await axios.post(url, servicioBody, {
      headers: getHeaders(servicioData.empresa || freematicaConfig.company)
    });
    
    console.log('📥 Respuesta de Freematica:', response.data);
    
    if (response.data && response.data.errorCode === '200') {
      console.log('\n✅ SERVICIO CREADO EXITOSAMENTE EN FREEMATICA');
      console.log('╚════════════════════════════════════════════════════════════╝\n');
      
      return {
        success: true,
        message: 'Servicio creado exitosamente en Freematica',
        data: response.data.data,
        servicioCreado: {
          contrato: servicioData.contrato,
          codigo: servicioData.codigo,
          descripcion: servicioData.descripcion,
          localizacion: `${servicioData.contrato}/${servicioData.codigo.toString().padStart(2, '0')}`
        }
      };
    } else {
      console.error('\n❌ ERROR AL CREAR SERVICIO');
      console.error('Mensaje:', response.data?.errorMessage);
      console.error('╚════════════════════════════════════════════════════════════╝\n');
      
      throw new Error(response.data?.errorMessage || 'Error al crear servicio en Freematica');
    }
  } catch (error) {
    console.error('❌ Error al crear servicio en Freematica:', error);
    if (error.response) {
      console.error('Detalles del error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }
    throw error;
  }
};

/**
 * Registra un servicio en Freematica a partir de los datos de una tienda de Sentry
 * Esta función busca primero el contrato y luego crea el servicio
 * @param {Object} tiendaData - Datos de la tienda/servicio desde Sentry
 * @param {string} tiendaData.localizacion - Localización en formato "CONTRATO/SERVICIO"
 * @param {string} tiendaData.nombre - Nombre de la tienda (usado como descripción)
 * @param {string} tiendaData.provincia - Provincia (para obtener código de delegación)
 * @param {string} tiendaData.direccion - Dirección del servicio (opcional, para observaciones)
 * @param {string} tiendaData.fechaAlta - Fecha de alta (opcional)
 * @param {Object} options - Opciones adicionales
 * @param {string} options.empresa - Código de empresa (opcional, default del config)
 * @param {string} options.tipo - Tipo de servicio (opcional)
 * @param {string} options.inspector - Código de inspector (opcional)
 * @returns {Promise<Object>} - Resultado del registro
 */
export const registrarServicioEnFreematica = async (tiendaData, options = {}) => {
  try {
    console.log('\n╔════════════════════════════════════════════════════════════╗');
    console.log('║   🏪 REGISTRANDO SERVICIO (TIENDA) EN FREEMATICA           ║');
    console.log('╚════════════════════════════════════════════════════════════╝');
    console.log('📋 Datos de la tienda:', tiendaData);
    console.log('📋 Opciones:', options);
    
    // Validar datos mínimos
    if (!tiendaData.localizacion) {
      throw new Error('Se requiere el campo localización en formato "CONTRATO/SERVICIO"');
    }
    if (!tiendaData.nombre) {
      throw new Error('Se requiere el nombre de la tienda');
    }
    
    // Extraer contrato y servicio de la localización
    const datosLocalizacion = extraerDatosLocalizacion(tiendaData.localizacion);
    const codigoContrato = datosLocalizacion.contrato;
    const codigoServicio = datosLocalizacion.servicio;
    
    console.log(`📋 Contrato: ${codigoContrato}`);
    console.log(`📋 Servicio: ${codigoServicio}`);
    
    // Obtener código de delegación desde la provincia
    const codigoDelegacion = tiendaData.provincia 
      ? obtenerCodigoProvincia(tiendaData.provincia)
      : options.delegacion || '00';
    
    console.log(`📋 Delegación (desde provincia "${tiendaData.provincia}"): ${codigoDelegacion}`);
    
    // 1. Buscar el contrato para obtener su idReg
    console.log('\n🔍 Paso 1: Buscando contrato en Freematica...');
    const resultadoContrato = await buscarContratoPorCodigo(codigoContrato, options.empresa);
    
    if (!resultadoContrato.success || !resultadoContrato.idReg) {
      throw new Error(`No se encontró el contrato ${codigoContrato} en Freematica. Debe crearse primero.`);
    }
    
    console.log(`✅ Contrato encontrado. idReg: ${resultadoContrato.idReg}`);
    
    // 2. Crear el servicio en el contrato
    console.log('\n🏢 Paso 2: Creando servicio en el contrato...');
    
    const servicioData = {
      empresa: options.empresa || resultadoContrato.contrato?.CTRT_EMP || freematicaConfig.company,
      delegacion: codigoDelegacion,
      contrato: codigoContrato,
      codigo: codigoServicio,
      descripcion: tiendaData.nombre.substring(0, 100), // Limitar a 100 caracteres
      inspector: options.inspector || '',
      tipo: options.tipo || '',
      fechaAlta: tiendaData.fechaAlta || new Date().toISOString().split('T')[0],
      fechaFin: tiendaData.fechaFin || '',
      observacionesPuesto: tiendaData.direccion 
        ? `Dirección: ${tiendaData.direccion.substring(0, 200)}` 
        : ''
    };
    
    const resultadoServicio = await crearServicioEnContrato(resultadoContrato.idReg, servicioData);
    
    console.log('\n✅ PROCESO COMPLETADO EXITOSAMENTE');
    console.log('╚════════════════════════════════════════════════════════════╝\n');
    
    return {
      success: true,
      message: 'Servicio registrado exitosamente en Freematica',
      contrato: {
        codigo: codigoContrato,
        idReg: resultadoContrato.idReg
      },
      servicio: resultadoServicio.servicioCreado,
      localizacion: `${codigoContrato}/${codigoServicio.toString().padStart(2, '0')}`
    };
    
  } catch (error) {
    console.error('\n❌ ERROR AL REGISTRAR SERVICIO EN FREEMATICA');
    console.error('Mensaje:', error.message);
    console.error('╚════════════════════════════════════════════════════════════╝\n');
    throw error;
  }
};

/**
 * Verifica si un servicio ya existe en Freematica
 * @param {string} localizacion - Localización en formato "CONTRATO/SERVICIO"
 * @param {string} empresa - Código de empresa (opcional)
 * @returns {Promise<Object>} - { existe: boolean, servicio: Object|null }
 */
export const verificarServicioExisteEnFreematica = async (localizacion, empresa = null) => {
  try {
    console.log(`🔍 Verificando si existe el servicio: ${localizacion}`);
    
    const resultado = await buscarServiciosPorLocalizacion([localizacion], empresa || freematicaConfig.company);
    
    if (resultado && resultado.data?.items && resultado.data.items.length > 0) {
      console.log('✅ El servicio ya existe en Freematica');
      return {
        existe: true,
        servicio: resultado.data.items[0]
      };
    }
    
    console.log('⚠️ El servicio NO existe en Freematica');
    return {
      existe: false,
      servicio: null
    };
  } catch (error) {
    console.error('❌ Error verificando servicio:', error);
    return {
      existe: false,
      servicio: null,
      error: error.message
    };
  }
};

export default {
  obtenerTagPersona,
  obtenerIdentificadoresServicio,
  extraerDatosLocalizacion,
  buscarServicioFreematica,
  buscarServiciosPorLocalizacion,
  validarFechasContrato,
  obtenerFestivosCalendario,
  obtenerCodigoCalendarioContrato,
  obtenerFestivosContrato,
  crearMarcaje,
  crearMarcajeTurno,
  crearMarcajesTurnoLote,
  actualizarCuadrante,
  convertirTurnosParaAPI,
  actualizarCuadranteCompleto,
  obtenerTramosPersona,
  obtenerTramosVigilantesServicio,
  obtenerTramosModificados,
  obtenerTramosModificadosServicio,
  obtenerCuadrantesFreematica,
  obtenerTurnosVigilantesFreematica,
  obtenerTurnosCuadrantes,
  obtenerTurnosCuadrantesInteligente,
  obtenerTurnosPersona,
  obtenerTurnosServicio,
  obtenerTurnosVigilantesServicio,
  // Nuevas funciones para guardar en BD
  guardarTurnoEnBD,
  guardarTurnosEnBD,
  obtenerYGuardarTurnos,
  obtenerYGuardarTurnosPersona,
  obtenerYGuardarTurnosServicio,
  calcularHorasTurno,
  actualizarHorasTurno,
  diagnosticarTurnosIndividuales,
  buscarDatosHistoricos,
  pushTurnosAFreematica,
  guardarTurnosYPushAFreematica,
  // Nuevas funciones para visualizar cuadrantes
  obtenerCuadrantesPorPersona,
  obtenerCuadrantesPorContrato,
  obtenerCuadrantesConPersonas,
  obtenerCuadrantesDetallesSolo,
  obtenerDetalleCuadrante,
  // Funciones actualizadas para fusionar turnos
  obtenerTurnosExistentesFreematica,
  obtenerTurnosExistentesFirestore,
  fusionarTurnos,
  actualizarCuadranteFreematica,
  // Nuevas funciones para gestión de contratos y servicios
  obtenerCodigoProvincia,
  obtenerListaContratos,
  buscarContratoPorCodigo,
  crearServicioEnContrato,
  registrarServicioEnFreematica,
  verificarServicioExisteEnFreematica,
  PROVINCIAS_CODIGOS
};